/* eslint-disable max-statements */
import { ListPageComponent } from '@icc/configurator/pages';
import { Injectable, Inject } from '@angular/core';
import { IccAccessoryAccessory, IccAccessoryColor } from '@icc/construction-elements/types';
import { ColorDto as IccColor } from '@icc/construction-elements/types';
import { TranslateService, ConfiguratorsDataService, APP_CONFIG, AppConfigFactory, EventBusService } from '@icc/shared';
import { _, castToNullableNumber, core, isArray, isDefined, isObject, isUndefined } from '@icc/shared/helpers';
import { ConfigurationsService, CurrentConfiguratorService, TimeLimitService } from '@icc/configurator/shared';
import { PriceService } from '@icc/price/b2b';
import { ColorMappingService } from '@icc/construction-elements/configurator';
import { HardwareTypesPageComponent } from '../../door/hardware-types-page/hardware-types-page.component';
import { HardwareTypeOptionsPageComponent } from '../../door/hardware-type-options-page/hardware-type-options-page.component';
import { DoorActiveConfiguration } from '@icc/window-door/types';
import { ModalService } from '@icc/configurator/shared';
import { LocksService } from './locks.service';
import { iccListTab } from '@icc/configurator/ui';
import { WindowAccessoriesService } from '../../accessories/accessories.service';
import { ElectronicLockPageComponent } from '../../door/electronic-lock-page/electronic-lock-page.component';
import { HardwareSet } from '@icc/window-door/types';
import { NewColorsService } from '@icc/construction-elements/configurator';
import { RecessedPullOptionsPageComponent } from '../../door/recessed-pull-type-options-page/recessed-pull-options-page.component';

@Injectable({
    providedIn: 'root',
})
export class HardwareService {
    private configurators = ['door'];

    doorHandleAdjustments: any[] = [];
    hardwareTypes: any[] = [];
    hardwareColors: IccAccessoryColor[] = [];
    electronicLocks: any[] = [];
    pulls: any[] = [];
    hinges: any[] = [];
    platesTypes: any = [];
    doorViewers: any = [];
    platesToMainLock: any = [];
    innerPlatesToMainLock: any = [];
    outerPlatesToMainLock: any = [];
    platesToAdditionalLock: any = [];
    innerPlatesToAdditionalLock: any = [];
    outerPlatesToAdditionalLock: any = [];
    plateKinds: any = [];
    cylindersToMainLock: any = [];
    cylindersToAdditionalLock: any = [];
    cylinderSystems: any = [];
    decoHingeCovers: any = [];
    loadedData = false;
    dependencies = [];
    isSystemOrPacketChangedInEditMode = false;
    colorIdToMapping = null;
    hardwareSets: HardwareSet[] = [];
    recessedPulls: any[] = [];

    constructor(
        private translateService: TranslateService,
        private modalService: ModalService,
        private configuratorsDataService: ConfiguratorsDataService,
        private currentConfiguratorService: CurrentConfiguratorService,
        private configurationsService: ConfigurationsService<'door'>,
        private priceService: PriceService,
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private colorMappingService: ColorMappingService,
        private eventBusService: EventBusService,
        private timeLimitService: TimeLimitService,
        private locksService: LocksService,
        private accessoriesService: WindowAccessoriesService,
        private newColorService: NewColorsService
    ) {
        if (this.configuratorsDataService.loaded) {
            this.init();
        }

        this.eventBusService.subscribeWithoutConfiguration(
            ['initializedConfigurator', 'loadedConfiguratorsData'],
            () => {
                this.init();
            }
        );

        this.eventBusService.subscribe(['accessoryColorBasedOnMapping'], (data) => {
            const value = data.value as {
                colorId: number;
                place: 'pull' | 'decoHingeCovers' | 'handle' | 'cylinder' | 'accessory' | 'plates' | 'outerPlates';
            };
            this.setColorsBasedOnColorMapping(value.colorId, value.place);
        });

        this.eventBusService.subscribe(
            ['changedFillings', 'setFillingColor', 'processedDependencies'],
            data => {
                if (DoorActiveConfiguration.is(data.activeConfiguration) && (data.activeConfiguration.System.door_type || data.activeConfiguration.System.door_hardware_type === 'hardware' ) && this.loadedData) {
                    const pausedEvents = ['processDependencies', 'changedDoorHardware'];
                    const pauseId = this.eventBusService.pause(pausedEvents);
                    this.setDefaultSet(data.activeConfiguration);
                    this.setDefaultDoorViewer(data.activeConfiguration);
                    this.validateAndFixIssues();
                    this.eventBusService.resume(pausedEvents, pauseId);
                }
            }
        );

        this.eventBusService.subscribe<unknown[]>(['setFrameProfile'], (data) => {
            if (
                DoorActiveConfiguration.is(data.activeConfiguration) &&
                (data.activeConfiguration.System.door_type || data.activeConfiguration.System.door_hardware_type === 'hardware')
            ) {
                const pausedEvents = ['processDependencies', 'changedDoorHardware'];
                const pauseId = this.eventBusService.pause(pausedEvents);
                this.validateAndFixIssues();
                this.eventBusService.resume(pausedEvents, pauseId);
            }
        });

        this.eventBusService.subscribe('setConstructionColor', (data) => {
            if (
                DoorActiveConfiguration.is(data.activeConfiguration)
                && (data.activeConfiguration.System.door_type
                    || data.activeConfiguration.System.door_hardware_type === 'hardware')
                && this.loadedData
            ) {
                const conf = data.activeConfiguration;

                const hardwareTypeItem = this.hardwareTypes.find(
                    t => Number(t.id) === conf.doorHardware.type
                );
                if (hardwareTypeItem && conf.doorHardware.colorIsDefault && this.itemHasCategoryMapping(hardwareTypeItem)) {
                    this.setDefaultColorForHardware(conf);
                }

                const plateItem = (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToMainLock : this.platesToMainLock).find(
                    t => Number(t.id) === conf.doorHardware.plates.mainLock
                );
                if (plateItem && conf.doorHardware.plates.colorIsDefault && this.itemHasCategoryMapping(plateItem)) {
                    this.setDefaultColorForPlate(conf);
                }

                const outerPlateItem = this.outerPlatesToMainLock.find(
                    t => Number(t.id) === conf.doorHardware.outerPlates.mainLock
                );
                if (outerPlateItem && conf.doorHardware.outerPlates.colorIsDefault && this.itemHasCategoryMapping(outerPlateItem)) {
                    this.setDefaultColorForOuterPlate(conf);
                }

                const pullItem = this.pulls.find(t => Number(t.id) === conf.doorHardware.pull);
                if (pullItem && conf.doorHardware.pullColorIsDefault && this.itemHasCategoryMapping(pullItem)) {
                    this.setDefaultColorForPull(conf);
                }

                const cylinderMainItem = this.cylindersToMainLock.find(
                    t => Number(t.id) === conf.doorHardware.cylinders.mainLock
                );
                if (cylinderMainItem && conf.doorHardware.cylinders.mainLockColorIsDefault && this.itemHasCategoryMapping(cylinderMainItem)) {
                    this.setDefaultColorForMainCylinder(conf);
                }

                const cylinderAdditionalItem = this.cylindersToAdditionalLock.find(
                    t => Number(t.id) === conf.doorHardware.cylinders.additionalLock
                );
                if (cylinderAdditionalItem && conf.doorHardware.cylinders.additionalLockColorIsDefault && this.itemHasCategoryMapping(cylinderAdditionalItem)) {
                    this.setDefaultColorForAdditionalCylinder(conf);
                }

                const doorViewerItem = this.doorViewers.find(
                    t => Number(t.id) === conf.doorHardware.doorViewer
                );
                if (doorViewerItem && conf.doorHardware.doorViewerColorIsDefault && this.itemHasCategoryMapping(doorViewerItem)) {
                    this.setDefaultColorForDoorViewer(conf);
                }

                const decoHingeCoverItem = this.decoHingeCovers.find(
                    t => Number(t.id) === conf.doorHardware.decorativeHingeCovers
                );
                if (decoHingeCoverItem && conf.doorHardware.decorativeHingeCoversColorIsDefault && this.itemHasCategoryMapping(decoHingeCoverItem)) {
                    this.setDefaultColorForDecorativeHingeCover(conf);
                }

                const electronicLockItem = this.electronicLocks.find(
                    t => Number(t.id) === conf.doorHardware.electronicLock?.id
                );
                if (electronicLockItem && conf.doorHardware.electronicLock.colorIsDefault && this.itemHasCategoryMapping(electronicLockItem)) {
                    this.setDefaultColorForElectronicLock(conf);
                }
            }
        });
    }

    validateAndFixIssues(conf = this.configurationsService.conf.Current) {
        const selectedFilling = this.getSelectedFilling(conf);
        const setDefaultPull = this.pulls.length && ['leverPull', 'doublePull'].indexOf(conf.doorHardware.set) > -1 && (!this.isPullBasedOnDependenciesAvailable() || !conf.doorHardware.pull);
        const removePull = selectedFilling?.integrated_pull && conf.doorHardware.pull;
        if (setDefaultPull || removePull) {
            this.setDefaultPull();
        }

        if (this.platesToMainLock.length && conf.doorHardware.plates.mainLock && !this.isPlateBasedOnDependencyAvailable()) {
            this.setDefaultPlateToMainLock();
        }

        const cylinders = this.getCylindersToMainLock(conf);
        const cylinderAvailable = cylinders.some(cylinder => Number(cylinder.id) === Number(conf.doorHardware.cylinders.mainLock));
        if(!cylinderAvailable || !this.isCylinderBasedOnDependencyAvailable()) {
            this.setDefaultCylinderToMainLock(conf);
        }

        const plates = this.getPlatesToMainLock(conf);
        const plateAvailable = plates.some(plate => Number(plate.id) === Number(conf.doorHardware.plates.mainLock));
        if(!plateAvailable) {
            this.setDefaultPlateToMainLock(conf);
        }

        if(this.doorViewers.length && !this.isDoorViewerBasedOnDependenciesAvailable()) {
            this.setDefaultDoorViewer(conf);
        }

        const recessedPulls = this.getRecessedPulls(conf);
        const isRecessedPullAvailable = recessedPulls.find(recessedPull => Number(recessedPull.id) === conf.doorHardware.recessedPull);
        if(!isRecessedPullAvailable) {
            this.setDefaultRecessedPull(conf);
        } else if(conf.doorHardware.recessedPull) {
            const recessedPullColors = this.getRecessedPullColors(conf);
            let colorAvailable = recessedPullColors.find(recessedPullColor =>
                recessedPullColor.colorSource === conf.doorHardware.recessedPullColor.colorSource
                && recessedPullColor.colors.some(recessedPullColorColor =>
                    Number(recessedPullColorColor.id) === Number(conf.doorHardware.recessedPullColor.color.id)
                )
            );

            if(!colorAvailable) {
                this.setDefaultColorForRecessedPull(conf);
                colorAvailable = recessedPullColors.find(recessedPullColor =>
                    recessedPullColor.colorSource === conf.doorHardware.recessedPullColor.colorSource
                    && recessedPullColor.colors.some(recessedPullColorColor =>
                        Number(recessedPullColorColor.id) === Number(conf.doorHardware.recessedPullColor.color.id)
                    )
                );
            }

            if(colorAvailable && (colorAvailable.price !== conf.doorHardware.recessedPullColor.price
                ||  colorAvailable.list_price !== conf.doorHardware.recessedPullColor.list_price)
            ) {
                conf.doorHardware.recessedPullColor.price = colorAvailable.price;
                conf.doorHardware.recessedPullColor.list_price = colorAvailable.list_price;
                this.priceService.count();
            }
        }

        if (conf.doorHardware.cylinders.additionalLock != null) {
            const additionalCylinder = this.cylindersToAdditionalLock.find(
                c => Number(c.id) === Number(conf.doorHardware.cylinders.additionalLock)
            );
            if (additionalCylinder) {
                this.setDefaultColorForAdditionalCylinder(conf);
            }
        }
    }



    /**
     * Funkcja inicjalizacyjna
     */
    init() {
        if (this.configurators.indexOf(this.currentConfiguratorService.conf) === -1) {
            return;
        }

        this.hardwareColors = this.configuratorsDataService.data.windowHandlesColors;
        this.cylinderSystems = this.configuratorsDataService.data.hardwareSystems || [];
        this.dependencies = this.configuratorsDataService.data.dependencies || [];
        this.hardwareSets = this.configuratorsDataService.data.hardwareSets || [];
        this.findHardwareBySystem();

        this.loadedData = true;
    }

    // eslint-disable-next-line max-statements
    findHardwareBySystem(conf = this.configurationsService.conf.Current) {
        if (!isObject(conf.System) || isUndefined(conf.System.id)) {
            return;
        }
        this.doorHandleAdjustments = [];
        this.hardwareTypes = [];
        this.pulls = [];
        this.hinges = [];
        this.electronicLocks = [];
        this.platesTypes = [];
        this.doorViewers = [];
        this.platesToMainLock = [];
        this.innerPlatesToMainLock = [];
        this.outerPlatesToMainLock = [];
        this.platesToAdditionalLock = [];
        this.innerPlatesToAdditionalLock = [];
        this.outerPlatesToAdditionalLock = [];
        this.plateKinds = [];
        this.cylindersToMainLock = [];
        this.cylindersToAdditionalLock = [];
        this.decoHingeCovers = [];
        this.recessedPulls = [];
        (this.configuratorsDataService.data.doorHandleAdjustments || []).forEach(lock => {
            const systemIds = lock.window_lines_ids;
            if (systemIds.indexOf(conf.System.id) > -1) {
                this.doorHandleAdjustments.push(lock);
            }
        });
        this.doorHandleAdjustments.unshift({
                id: null,
                name: this.translateService.instant('INTERFACE|Brak'),
        });
        (this.configuratorsDataService.data.hardwareTypes || []).forEach(lock => {
            const systemIds = lock.window_lines_ids;
            if (systemIds.indexOf(conf.System.id) > -1) {
                this.hardwareTypes.push(lock);
            }
        });
        (this.configuratorsDataService.data.hinges || []).forEach(hinge => {
            const systemIds = hinge.window_lines_ids;
            if (systemIds.indexOf(conf.System.id) > -1) {
                this.hinges.push(hinge);
            }
        });
        (this.configuratorsDataService.data.pullType || []).forEach(item => {
            const systemIds = item.window_lines_ids;
            if (systemIds.indexOf(conf.System.id) > -1) {
                this.pulls.push(item);
            }
        });
        (this.configuratorsDataService.data.electronicLocks || []).forEach(item => {
            const systemIds = item.window_lines_ids;
            if (systemIds.indexOf(conf.System.id) > -1) {
                this.electronicLocks.push(item);
            }
        });
        (this.configuratorsDataService.data.plateTypes || []).forEach(item => {
            const systemIds = item.window_lines_ids;
            if (systemIds.indexOf(conf.System.id) > -1) {
                this.platesTypes.push(item);
            }
        });
        if (!conf.System.always_with_viewer_if_available) {
            this.doorViewers.push({
                id: 0,
                name: this.translateService.instant('INTERFACE|Brak'),
            });
        }
        (this.configuratorsDataService.data.doorViewers || []).forEach(item => {
            const systemIds = item.window_lines_ids;
            if (systemIds.indexOf(conf.System.id) > -1) {
                this.doorViewers.push(item);
            }
        });
        (this.configuratorsDataService.data.plates || []).forEach(item => {
            const systemIds = item.window_lines_ids;
            if (systemIds.indexOf(conf.System.id) > -1) {
                if (item.fits_the_lock) {
                    if(item.plate_side === 'doublePlate') {
                        this.platesToAdditionalLock.push(item);
                    } else if(item.plate_side === 'innerPlate') {
                        this.innerPlatesToAdditionalLock.push(item);
                    } else if(item.plate_side === 'outerPlate') {
                        this.outerPlatesToAdditionalLock.push(item);
                    }
                } else {
                       if(item.plate_side === 'doublePlate') {
                        this.platesToMainLock.push(item);
                    } else if(item.plate_side === 'innerPlate') {
                        this.innerPlatesToMainLock.push(item);
                    } else if(item.plate_side === 'outerPlate') {
                        this.outerPlatesToMainLock.push(item);
                    }
                    if (!this.plateKinds.includes(item.plate_type)) {
                        this.plateKinds.push(item.plate_type);
                    }
                }
            }
        });
        (this.configuratorsDataService.data.cylinders || []).forEach(item => {
            const systemIds = item.window_lines_ids;
            if (systemIds.indexOf(conf.System.id) > -1) {
                if (item.fits_the_lock) {
                    this.cylindersToAdditionalLock.push(item);
                } else {
                    this.cylindersToMainLock.push(item);
                }
            }
        });
        (this.configuratorsDataService.data.decoHingeCovers || []).forEach(item => {
            const systemIds = item.window_lines_ids;
            if (systemIds.indexOf(conf.System.id) > -1) {
                this.decoHingeCovers.push(item);
            }
        });
           (this.configuratorsDataService.data.recessedPulls || []).forEach(recessedPull => {
            const systemIds = recessedPull.window_lines_ids;
            if (systemIds.indexOf(conf.System.id) > -1) {
                this.recessedPulls.push(recessedPull);
            }
        });

        if (!this.configurationsService.conf.Edit
            || (
                this.configurationsService.conf.Edit
                && this.isSystemOrPacketChangedInEditMode)
            ) {
            this.setDefaultDoorHandleAdjustments(conf);
            this.setDefaultType(conf);
            this.setDefaultElectronicLock(conf);
            this.setDefaultDoorViewer(conf);
            this.setDefaultSet(conf);
            this.setDefaultPlateType(conf);
            this.setDefaultPlateToMainLock(conf);
            this.setDefaultPlateToAdditionalLock(conf);
            this.setDefaultOuterPlateToAdditionalLock(conf);
            this.setDefaultCylinderToMainLock(conf);
            this.setDefaultDecorativeHingeCover(conf);
            this.setDefaultPull(conf);
            if (!conf.System.available_door_viewer_punching) {
                this.changeDoorViewerPunching(false, conf)
            }
        } else {
            this.setDefaultHardwareForEdit(conf);
        }
    }

    setDefaultHardwareForEdit(conf = this.configurationsService.conf.Current) {
        this.setDefaultDoorHandleAdjustments(conf);
        this.setDefaultElectronicLock(conf);
        this.setDefaultDoorViewer(conf);
        this.setDefaultSet(conf);
        this.setDefaultPlateType(conf);

        if(conf.doorHardware.plates.mainLock) {
            const plateToMainLockCorrect = (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToMainLock: this.platesToMainLock).some(mainLock => Number(mainLock.id) === Number(conf.doorHardware.plates.mainLock));
            if(!plateToMainLockCorrect) {
                this.setDefaultPlateToMainLock(conf);
            }
        }
        if(conf.doorHardware.plates.additionalLock) {
            const plateToAdditionalLockCorrect =  this.platesToAdditionalLock.some(additionalLock => Number(additionalLock.id) === Number(conf.doorHardware.plates.additionalLock));
            if(!plateToAdditionalLockCorrect) {
                this.setDefaultPlateToAdditionalLock(conf);
            }
        }
        this.setDefaultCylinderToMainLock(conf);
        this.setDefaultDecorativeHingeCover(conf);
    }

    openDoorHandleAdjustmentsModal(conf = this.configurationsService.conf.Current) {
        const modalInstance = this.modalService.open({
            pageComponent: ListPageComponent,
            resolve: {
                title: () => _('DOOR|Przystosowanie pod pochwyt'),
                selectedId: () => conf.doorHardware.doorHandleAdjustment,
                items: () => this.doorHandleAdjustments,
                imageUrl: () => '/files/windowaccessory',
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected)) {
                this.setDoorHandleAdjustments(selected.id);
            }
        });
    }

    setDefaultDoorHandleAdjustments(conf = this.configurationsService.conf.Current) {
        const adjustments = this.doorHandleAdjustments;
        if (adjustments.length > 0 && conf.doorHardware.set === 'leverPull' && conf.doorHardware.type) {
            if (!adjustments.map(p => castToNullableNumber(p.id)).includes(conf.doorHardware.doorHandleAdjustment)) {
                this.setDoorHandleAdjustments(adjustments[0].id);
            }
        } else {
            this.setDoorHandleAdjustments(null);
        }
    }

    setDoorHandleAdjustments(id: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.doorHandleAdjustment = castToNullableNumber(id);
        this.setDefaultType(conf);
        this.emitEvent();
    }

    getTypes(conf = this.configurationsService.conf.Current) {
        const types = this.hardwareTypes.filter(type =>
            type.accessories
                .map(castToNullableNumber)
                .includes(conf.doorHardware.doorHandleAdjustment)
            && (type.hardware_sets || []).includes(conf.doorHardware.set)
        );
        types.push({
            id: null,
            name: this.translateService.instant('INTERFACE|Brak'),
        });
        return types;
    }

    openModalType(conf = this.configurationsService.conf.Current) {
        const matchedColor = this.hardwareColors.find(
            (el) => Number(el.id) === conf.doorHardware.color
        );
        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypesPageComponent,
            resolve: {
                title: () => _('DOOR|Wybierz typ osprzętu'),
                types: () => this.getTypes(conf),
                selType: () => conf.doorHardware.type,
                hardwareColors: () => this.hardwareColors,
                selColor: () => matchedColor,
                colorIdToMapping: () => this.colorIdToMapping
            },
        });
        if (this.config().IccConfig.Configurators.tutorialAvailable) {
            this.eventBusService.post({
                key: 'tutorialSteps',
                value: 'modalHinge',
            });
        }

        modalInstance.result.then(selectedData => {
            if (isObject(selectedData) && isDefined(selectedData.type)) {
                this.setType(selectedData.type.id);
                this.setHardwareColorById(selectedData.color, selectedData.defaultColor);
                this.eventBusService.post({ key: 'processDependencies', value: null });
                this.priceService.count();
            }
        });

        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg',
                });
            }
        });
    }

    setDefaultType(conf = this.configurationsService.conf.Current) {
        const hardwareTypes = this.getTypes(conf);
        if (hardwareTypes.length > 0) {
            this.setType(castToNullableNumber(hardwareTypes[0].id), conf);
            this.setDefaultColorForHardware(conf);
        } else {
            this.setType(null, conf);
        }
    }

    setType(type: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.type = castToNullableNumber(type);
        this.setDefaultElectronicLock(conf);
        this.setDefaultPull(conf);
        this.setDefaultPlateType(conf);
        this.setDefaultPlateToMainLock(conf);
        this.setDefaultCylinderToMainLock(conf);
        this.setDefaultDecorativeHingeCover(conf);
        this.emitEvent();
    }

    openModalTypeOptions() {
        const matchedColor = this.hardwareColors.find(
            (el) => Number(el.id) === this.configurationsService.conf.Current.doorHardware.color
        );
        const hardwareTypeId =
            this.configurationsService.conf.Current.doorHardware
            && this.configurationsService.conf.Current.doorHardware.type;
        const type = this.hardwareTypes.find(f => Number(f.id) === Number(hardwareTypeId));
        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypeOptionsPageComponent,
            resolve: {
                title: () => _('DOOR|Opcje osprzętu'),
                selectedType: () => type,
                hardwareColors: () => this.hardwareColors,
                selColor: () => matchedColor,
            },
        });

        modalInstance.result.then(selectedData => {
            if (isObject(selectedData) && isDefined(selectedData.type)) {
                this.setHardwareColorById(selectedData.color, selectedData.defaultColor);
            }
            this.priceService.count();
            this.timeLimitService.count();
        });
        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg',
                });
            }
        });
    }

    /**
     * Funkcja ustawiajca kolor osprzętu
     * @param {object} color Kolor
     */
    setHardwareColor(
        color: IccAccessoryColor | null,
        conf = this.configurationsService.conf.Current
    ) {
        conf.doorHardware.color = color ? Number(color.id) : null;
        conf.doorHardware.colorIsDefault = color?.isDefault ?? false;
        this.setDefaultColorForPlate(conf);
        this.setDefaultColorForPull(conf);
        this.setDefaultColorForMainCylinder(conf);
        if (color) {
            this.setColorsBasedOnColorMapping(Number(color.id));
        }
        this.emitEvent();
    }

    /**
     * Funkcja ustawiająca domyslny kolor dla osprzętu
     */
    setDefaultColorForHardware(conf = this.configurationsService.conf.Current) {
        const type = this.hardwareTypes.find(t => Number(t.id) === conf.doorHardware.type);

        if (isObject(type) && isArray(type.colors_ids) && isArray(this.hardwareColors)) {
            const categoryColor = this.findColorFromCategoryMapping(type, conf);
            if (categoryColor) {
                this.setHardwareColor(categoryColor, conf);
                return;
            }
            const windowColorId = this.colorMappingService.getWindowColorId(conf, 'inner');
            const matchedColors = this.colorMappingService.getColors(
                Number(windowColorId),
                'window',
                'accessory'
            );
            const colors = this.hardwareColors.filter(el => type.colors_ids.indexOf(el.id) > -1);
            if (isArray(colors) && isDefined(colors[0])) {
                const windowColors = matchedColors
                    .map(m => colors.filter(c => Number(c.id) === m)[0])
                    .filter(m => m);
                let color;
                if (isArray(windowColors) && isDefined(windowColors[0])) {
                    color = core.copy(windowColors[0]);
                } else {
                    color = core.copy(colors[0]);
                }
                color.isDefault = true;
                this.setHardwareColor(color, conf);
            }
        } else {
            this.setHardwareColor(null, conf);
        }
    }

    /**
     * Funkcja ustawiająca kolor dla osprzętu
     * @param  {number} colorId  Id koloru
     */
    setHardwareColorById(colorId, isDefault = false) {
        const matchedColors = this.hardwareColors.filter(el => el.id === colorId);
        if (matchedColors.length > 0) {
            const color = core.copy(matchedColors[0]);
            color.isDefault = isDefault;
            this.setHardwareColor(color);
        } else {
            this.setHardwareColor(null);
        }
    }

    hardwareTypeBoxSelect = (conf: DoorActiveConfiguration) => {
        const type = this.getTypes(conf).find(
            t => castToNullableNumber(t.id) === conf.doorHardware.type
        );
        const matchedColor = this.hardwareColors.find(
            (el) => Number(el.id) === conf.doorHardware.color
        );
        const img = type ? '/files/windowaccessory/' + type.img : '';

        const name = type ? type.name : '';
        const color = matchedColor ? matchedColor.name : null;
        const colorId = matchedColor ? matchedColor.id : null;
        const description = type ? type.description : '';
        return {
            img,
            name,
            color,
            colorId,
            description,
        };
    };

    setSet(
        set: null | 'leverKnob' | 'doubleLever' | 'leverPull' | 'doublePull' | 'mixedLever',
        conf = this.configurationsService.conf.Current
    ) {
        conf.doorHardware.set = set;
        conf.doorHardware.setName = this.hardwareSets.find(s => s.setId === set)?.name;
        this.setDefaultType();
        this.setDefaultDoorHandleAdjustments();
        this.setDefaultPull();
        this.setDefaultRecessedPull();
        this.setDefaultPlateToMainLock();
        this.setDefaultDecorativeHingeCover();
        this.setDefaultColorForHardware();
        this.emitEvent();
    }

    setDefaultSet(conf: DoorActiveConfiguration) {
        const availableSets = this.getAvailableSets(conf).map(s => (s === 'none' ? null : s));
        if (!availableSets.includes(conf.doorHardware.set) || conf.doorHardware.set === null) {
            this.setSet(availableSets[0] as 'leverKnob' | 'doubleLever' | 'leverPull' | 'doublePull', conf);
        }
    }

    getAvailableSets(conf: DoorActiveConfiguration) {
        const selectedFilling = this.getSelectedFilling(conf);
        const availableSets =  (conf.System.hardware_sets || [])
            .filter(s => s !== 'leverPull' || !selectedFilling || selectedFilling.has_pull)
            .filter(s => s !== 'leverRecessedPull' || this.getRecessedPulls(conf).length > 0);

        if(selectedFilling?.integrated_pull) {
            return this.hardwareSets.filter(hardwareSet =>hardwareSet.setId === 'leverPull').map(s => s.setId);
        }
        return this.hardwareSets.filter(hardwareSet => availableSets.includes(hardwareSet.setId)).map(s => s.setId);
    }

    openPullsModal() {
        const matchedColor = this.hardwareColors.find(el => (Number(el.id) === this.configurationsService.conf.Current.doorHardware.pullColor));
        const handleType = this.configurationsService.conf.Current.doorHardware.set === 'leverPull' ? 'pull' : 'doublePull';
        const pulls = core.copy(this.pulls).filter(pull => pull.handle_type === handleType);
        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypesPageComponent,
            resolve: {
                title: () => _('DOOR|Pochwyty'),
                types: () => pulls,
                tabs: () => this.getTabsBasedOnWindowAccessoriesCategoryId(pulls),
                selType: () => this.configurationsService.conf.Current.doorHardware.pull,
                hardwareColors: () => this.hardwareColors,
                selColor: () => matchedColor,
                canOpenOptions: () => (type: IccAccessoryAccessory) => !type.pull_color_related_to_plate,
                colorIdToMapping: () => this.colorIdToMapping
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected.type)) {
                this.setPull(Number(selected.type.id), selected.type.pull_color_related_to_plate);
                if (!selected.type.pull_color_related_to_plate && selected.type?.colors_ids) {
                    this.setPullColorById(selected.color, selected.defaultColor);
                } else {
                    this.setDefaultColorForPull();
                }
                this.eventBusService.post({ key: 'processDependencies', value: null });
                this.priceService.count();
            }
        });
    }

    setDefaultPull(conf = this.configurationsService.conf.Current) {
        const selectedFilling = this.getSelectedFilling(conf);
        if (conf.doorHardware.set === 'leverPull' && !selectedFilling) {
            return;
        }
        if (this.pulls.length > 0 && conf.doorHardware.set === 'leverPull' && !selectedFilling.integrated_pull) {
            if (!this.pulls.filter(pull => pull.handle_type === 'pull').map(p => castToNullableNumber(p.id)).includes(conf.doorHardware.pull) || !this.isPullBasedOnDependenciesAvailable()) {

                const pull = this.getPull(conf, 'pull');
                this.setPull(Number(pull.id), pull.pull_color_related_to_plate);

                if (!pull.pull_color_related_to_plate && pull?.colors_ids?.length > 0 && pull?.colors_ids[0]) {
                    this.setPullColorById(pull.colors_ids[0]);
                } else {
                    this.setDefaultColorForPull();
                }
            }
        } else if (this.pulls.length > 0 && conf.doorHardware.set === 'doublePull') {
            if (!this.pulls.filter(pull => pull.handle_type === 'doublePull').map(p => castToNullableNumber(p.id)).includes(conf.doorHardware.pull) || !this.isPullBasedOnDependenciesAvailable()) {

                const pull = this.getPull(conf, 'doublePull');
                this.setPull(Number(pull.id), pull.pull_color_related_to_plate);

                if (!pull.pull_color_related_to_plate && pull?.colors_ids?.length > 0 && pull?.colors_ids[0]) {
                    this.setPullColorById(pull.colors_ids[0]);
                } else {
                    this.setDefaultColorForPull();
                }
            }
        } else {
            this.setPull(null, null);
        }
    }


    getPull(conf = this.configurationsService.conf.Current, type: 'pull' | 'doublePull') {
        const pullCategoryId = this.pulls.find(
            (p) =>
                Number(p.id) === Number(conf.doorHardware?.pull)
                && p.handle_type === type
        )?.window_accessories_category_id;
        const pullBaseOnCategory = pullCategoryId ? this.pulls.find(
                (p) =>
                    this.isPullBasedOnDependenciesAvailable(Number(p.id))
                    && Number(p?.window_accessories_category_id) === Number(pullCategoryId)
                    && p.handle_type === type
            ) : false
        const pull = this.pulls.find((p) => this.isPullBasedOnDependenciesAvailable(Number(p.id)) && p.handle_type === type);

        return pullBaseOnCategory ? pullBaseOnCategory : pull;
    }

    isPlateBasedOnDependencyAvailable(plateId = this.configurationsService.conf.Current.doorHardware?.plates?.mainLock) {
        let isPlateAvailable = true;

        const blockedItems = this.configurationsService.conf.Current.Dependencies.filter(
            (p) => p.type === 'blockade_to_configuration' || p.type === 'blockadeAccessory'
        );

        if (Array.isArray(blockedItems) && !blockedItems.length || !plateId) {
            return isPlateAvailable;
        }

        this.dependencies.forEach(dependency => {
            blockedItems.forEach(blocked => {
                if (dependency && blocked && dependency.id === Number(String(blocked.id).split('-')[0])) {
                    if(blocked.type === 'blockadeAccessory') {
                        const accessoriesId = dependency.actions.find((p) => p.type === 'blockadeAccessory')?.value || [];
                        if(plateId && accessoriesId.map(Number).includes(Number(plateId))) {
                            isPlateAvailable = false;
                        }
                    } else {
                        dependency.conditions.forEach(condition => {
                            if (condition.type) {
                                if (plateId && condition.value && Number(plateId) === Number(condition.value)) {
                                    isPlateAvailable = false;
                                }
                            }
                        })
                    }
                }
            });
        });

        return isPlateAvailable;
    }

    isPullBasedOnDependenciesAvailable(pullId = this.configurationsService.conf.Current.doorHardware?.pull) {
        let isPullAvailable = true;

        const blockedItems = this.configurationsService.conf.Current.Dependencies.filter(
            (p) => p.type === 'blockade_to_configuration' || p.type === 'blockadeAccessory'
        );

        if (Array.isArray(blockedItems) && !blockedItems.length || !pullId) {
            return isPullAvailable;
        }

        this.dependencies.forEach(dependency => {
            blockedItems.forEach(blocked => {
                if (dependency && blocked && dependency.id === Number(String(blocked.id).split('-')[0])) {
                    if(blocked.type === 'blockadeAccessory') {
                        const accessoriesId = dependency.actions.find((p) => p.type === 'blockadeAccessory')?.value || [];
                       if(pullId && accessoriesId.map(Number).includes(Number(pullId))) {
                           isPullAvailable = false;
                       }
                    } else {
                        dependency.conditions.forEach(condition => {
                            if (condition.type === 'pull') {
                                if (pullId && condition.value && pullId === Number(condition.value)) {
                                    isPullAvailable = false;
                                }
                            }
                        })
                    }
                }
            });
        });

        return isPullAvailable;
    }

    isRecessedPullBasedOnDependenciesAvailable(recessedPullId = this.configurationsService.conf.Current.doorHardware?.recessedPull) {
        let isRecessedPullAvailable = true;

        const blockedItems = this.configurationsService.conf.Current.Dependencies.filter(
            (p) => p.type === 'blockade_to_configuration' || p.type === 'blockadeAccessory'
        );

        if (Array.isArray(blockedItems) && !blockedItems.length || !isRecessedPullAvailable) {
            return isRecessedPullAvailable;
        }

        this.dependencies.forEach(dependency => {
            blockedItems.forEach(blocked => {
                if (dependency && blocked && dependency.id === Number(String(blocked.id).split('-')[0])) {
                    if(blocked.type === 'blockadeAccessory') {
                        const accessoriesId = dependency.actions.find((p) => p.type === 'blockadeAccessory')?.value || [];
                       if(recessedPullId && accessoriesId.map(Number).includes(Number(recessedPullId))) {
                           isRecessedPullAvailable = false;
                       }
                    } else {
                        dependency.conditions.forEach(condition => {
                            if (condition.type === 'pull') {
                                if (recessedPullId && condition.value && recessedPullId === Number(condition.value)) {
                                    isRecessedPullAvailable = false;
                                }
                            }
                        })
                    }
                }
            });
        });

        return isRecessedPullAvailable;
    }

    isDoorViewerBasedOnDependenciesAvailable(doorViewerId = this.configurationsService.conf.Current.doorHardware?.doorViewer) {
        let isDoorViewerAvailable = true;

        const blockedItems = this.configurationsService.conf.Current.Dependencies.filter(
            (p) => p.type === 'blockadeAccessory'
        );

        if (Array.isArray(blockedItems) && !blockedItems.length || !doorViewerId) {
            return isDoorViewerAvailable;
        }

        this.dependencies.forEach(dependency => {
            blockedItems.forEach(blocked => {
                if (dependency && blocked && dependency.id === Number(String(blocked.id).split('-')[0])) {
                    const accessoriesId = dependency.actions.find((p) => p.type === 'blockadeAccessory')?.value || [];
                    if(doorViewerId && accessoriesId.map(Number).includes(Number(doorViewerId))) {
                        isDoorViewerAvailable = false;
                    }
                }
            });
        });

        return isDoorViewerAvailable;
    }

    isCylinderBasedOnDependencyAvailable(cylinderId = this.configurationsService.conf.Current.doorHardware?.cylinders?.mainLock) {
        let isCylinderAvailable = true;

        const blockedItems = this.configurationsService.conf.Current.Dependencies.filter(
            (p) => p.type === 'blockadeAccessory'
        );

        if (Array.isArray(blockedItems) && !blockedItems.length || !cylinderId) {
            return isCylinderAvailable;
        }

        this.dependencies.forEach(dependency => {
            blockedItems.forEach(blocked => {
                if (dependency && blocked && dependency.id === Number(String(blocked.id).split('-')[0])) {
                    const accessoriesId = dependency.actions.find((p) => p.type === 'blockadeAccessory')?.value || [];
                    if(cylinderId && accessoriesId.map(Number).includes(Number(cylinderId))) {
                        isCylinderAvailable = false;
                    }
                }
            });
        });

        return isCylinderAvailable;
    }

    setPull(id: number | null, pullColorRelatedToPlate: boolean | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.pull = id;
        conf.doorHardware.pullColorRelatedToPlate = pullColorRelatedToPlate;
        this.emitEvent();
        this.eventBusService.post({ key: 'processDependencies', value: null });
    }

    setDefaultRecessedPull(conf = this.configurationsService.conf.Current) {
        const recessedPulls = this.getRecessedPulls(conf);
        if (conf.doorHardware.set === 'leverRecessedPull' && this.recessedPulls.length > 0) {
            this.setRecessedPull(Number(recessedPulls[0].id));
            this.setDefaultColorForRecessedPull();
        } else {
            this.setRecessedPull(null);
        }
    }

    setDefaultColorForRecessedPull(conf = this.configurationsService.conf.Current) {
        const recessedPull = this.recessedPulls.find(t => Number(t.id) === conf.doorHardware.recessedPull)
        const colors = this.getRecessedPullColors(conf);

        if (isObject(recessedPull) && colors.length > 0) {
            this.setColorForRecessedPull(colors[0].colorSource, colors[0].colors[0], true, colors[0].price, colors[0].list_price)
        } else {
            conf.doorHardware.recessedPullColor = null
        }
    }

    setRecessedPull(id: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.recessedPull = id;
        this.emitEvent();
        this.eventBusService.post({ key: 'processDependencies', value: null });
    }

    setColorForRecessedPull(colorSource: string, color: IccAccessoryColor | IccColor, isDefault, price: number, list_price: number) {
        const conf = this.configurationsService.conf.Current
        conf.doorHardware.recessedPullColor = {
            colorSource,
            color,
            isDefault,
            price,
            list_price
        }
        this.emitEvent();
    }

    openLockModalInSash(lockBox) {
        const modalInstance = this.modalService.open({
            pageComponent: ListPageComponent,
            resolve: {
                title: () => _('DOOR|Wybór zamka'),
                selectedId: () => lockBox.sash.lock.id,
                items: () => this.locksService.locks,
                imageUrl: () => '/files/windowaccessory',
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected)) {
                lockBox.sash.lock = selected;
                if (this.config().IccConfig.Configurators.dependencies) {
                    this.eventBusService.post({ key: 'processDependencies', value: null });
                }
                this.priceService.count();
            }
            this.timeLimitService.count();
        });

        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg',
                });
            }
        });
    }

    openLockModal(conf = this.configurationsService.conf.Current) {

        const modalInstance = this.modalService.open({
            pageComponent: ListPageComponent,
            resolve: {
                title: () => _('DOOR|Wybór zamka'),
                selectedId: () => conf.Lock.id,
                items: () => this.locksService.locks,
                imageUrl: () => '/files/windowaccessory',
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected)) {
                this.locksService.selectLock(selected);
                this.setDefaultPlateToMainLock();
            }
            this.timeLimitService.count();
        });

        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg',
                });
            }
        });
    }

    openHardwareSetModal(conf = this.configurationsService.conf.Current) {
        const availableSets = this.getAvailableSets(conf);
        const availableHardwareSets = this.hardwareSets.filter(hardwareSet => availableSets.includes(hardwareSet.setId));
        const selectedHardwareSetId = this.hardwareSets.find(hardwareSet => hardwareSet.setId === conf.doorHardware.set)?.id;
        const modalInstance = this.modalService.open({
            pageComponent: ListPageComponent,
            resolve: {
                title: () => _('DOOR|Wybór osprzętu'),
                selectedId: () => selectedHardwareSetId,
                items: () => availableHardwareSets,
                imageUrl: () => '/files/hardwareset',
                itemDescriptionPropertyName: 'description',
                itemShortDescriptionPropertyName: 'shortDescription',
                showItemInfo: true
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected)) {
                const selectedHardwareSet = this.hardwareSets.find(hardwareSet => hardwareSet.id === selected.id).setId;
                this.setSet(selectedHardwareSet as | 'leverKnob' | 'doubleLever' | 'leverPull' | 'doublePull' | 'mixedLever');
            }
        });
    }

    openElectronicLockModal(conf = this.configurationsService.conf.Current) {
        const matchedColor = this.hardwareColors.find(
            (el) =>
                Number(el.id) === this.configurationsService.conf.Current.doorHardware.electronicLock?.color
        );

        const modalInstance = this.modalService.open({
            pageComponent: ElectronicLockPageComponent,
            resolve: {
                title: () => _('DOOR|Elektroniczne wkładki'),
                items: () => this.electronicLocks,
                selectedId: () => conf.doorHardware.electronicLock,
                selColor: () => matchedColor,
                hardwareColors: () => this.hardwareColors,
                imageUrl: () => '/files/windowaccessory',
            },
        });

        modalInstance.result.then(selected => {
            if (isDefined(selected)) {
                this.setElectronicLockColorById(Number(selected.color), selected.defaultColor);
                this.setElectronicLock(Number(selected.electronicLock?.id));
                this.eventBusService.post({ key: 'processDependencies', value: null })
                this.priceService.count();
            }
        });
    }

    openElectronicLockOptions(conf = this.configurationsService.conf.Current) {
        const electronicLock = this.electronicLocks.find(lock => Number(lock.id) === conf.doorHardware.electronicLock?.id)
        const matchedColor = this.hardwareColors.find(el => Number(el.id) === conf.doorHardware.electronicLock?.color);


        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypeOptionsPageComponent,
            resolve: {
                title: () => _('DOOR|Opcje elektronicznej wkładki'),
                selectedType: () => electronicLock,
                items: () => this.electronicLocks,
                imageUrl: () => '/files/windowaccessory',
                hardwareColors: () => this.hardwareColors,
                selColor: () => matchedColor,
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected)) {
                this.setElectronicLockColorById(Number(selected.color), selected.defaultColor);
            }
            this.priceService.count();
            this.timeLimitService.count();
        });

        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg'
                })
            }
        })
    }

    setDefaultElectronicLock(conf = this.configurationsService.conf.Current) {
        const type = this.hardwareTypes.find(f => Number(f.id) === Number(conf.doorHardware.type));
        const cylinder = this.cylindersToMainLock.find(
            f => Number(f.id) === Number(conf.doorHardware.cylinders.mainLock)
        );
        if (
            this.electronicLocks.length > 0
            && ((type && type.electronic_door_lock) || (cylinder && cylinder.electronic_door_lock))
        ) {
            if (!this.electronicLocks.map(p => castToNullableNumber(p.id)).includes(conf.doorHardware.electronicLock?.id)) {
                this.setElectronicLock(Number(this.electronicLocks[0].id));
                this.setDefaultColorForElectronicLock(conf);
            }
        } else {
            this.setElectronicLock(null);
        }
    }

    setElectronicLock(id: number | null, conf = this.configurationsService.conf.Current) {
        if(!id) {
            conf.doorHardware.electronicLock = {id, color: null, colorIsDefault: true};
        } else if (conf.doorHardware.electronicLock) {
            conf.doorHardware.electronicLock.id = id;
        } else {
            conf.doorHardware.electronicLock = {
                id,
                color: null,
                colorIsDefault: true,
            };
        }
        this.emitEvent();
    }

    openPlateTypeModal(conf = this.configurationsService.conf.Current) {
        const modalInstance = this.modalService.open({
            pageComponent: ListPageComponent,
            resolve: {
                title: () => _('DOOR|Typy oszyldowania'),
                selectedId: () => conf.doorHardware.plateType,
                items: () => this.getPlateTypes(conf),
                imageUrl: () => '/files/windowaccessory',
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected)) {
                this.setPlateType(Number(selected.id));
            }
        });
    }

    setDefaultPlateType(conf = this.configurationsService.conf.Current) {
        const platesTypes = this.getPlateTypes(conf);
        if (
            platesTypes.length > 0
            && !platesTypes.map(p => castToNullableNumber(p.id)).includes(conf.doorHardware.plateType)
        ) {
            this.setPlateType(Number(platesTypes[0].id));
        } else if (platesTypes.length === 0) {
            this.setPlateType(null);
        }
    }

    setPlateType(id: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.plateType = id;
        this.emitEvent();
    }

    getPlateTypes(conf = this.configurationsService.conf.Current) {
        return this.platesTypes.filter(type =>
            type.accessories.map(Number).includes(conf.doorHardware.type)
        );
    }

    openDoorViewerModal(conf = this.configurationsService.conf.Current) {
        const matchedColor = this.hardwareColors.find(color => Number(color.id) === conf.doorHardware.doorViewerColor);
        const doorViewerId = conf.doorHardware && conf.doorHardware.doorViewer;
        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypesPageComponent,
            resolve: {
                title: () => _('DOOR|Wizjery'),
                types: () => this.getAvailableDoorViewers(conf),
                selType: () => doorViewerId,
                selColor: () => matchedColor,
                defaultImageUrl: () => '/img/door/wizjer_brak.jpg',
                hardwareColors: () => this.hardwareColors,
                colorIdToMapping: () => this.colorIdToMapping
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected.type)) {
                this.setDoorViewer(selected.type.id);
                if (selected?.color) {
                    this.setDoorViewerColorById(selected.color, selected.defaultColor);
                }
            }
        });
    }

    setDefaultDoorViewer(conf = this.configurationsService.conf.Current) {
        const doorViewers = this.getAvailableDoorViewers(conf).filter(viewier => this.isDoorViewerBasedOnDependenciesAvailable(viewier.id));
        if (doorViewers.filter(viewier => viewier.id !== 0 ).length > 0) {
            if (!conf.doorHardware.doorViewer || !doorViewers.map(p => castToNullableNumber(p.id)).includes(conf.doorHardware.doorViewer)) {
                this.setDoorViewer(doorViewers[0].id);
            }
        } else {
            this.setDoorViewer(null);
        }
    }

    setDoorViewer(id: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.doorViewer = id === 0 ? id : Number(id);
        if (!conf.doorHardware.doorViewer) this.changeDoorViewerPunching(false, conf);
        this.setDefaultColorForDoorViewer(conf);
        this.eventBusService.post({ key: 'processDependencies', value: null });
        this.emitEvent();
    }

    openDoorViewerOptionsModal(conf = this.configurationsService.conf.Current) {
        const matchedColor = this.hardwareColors.find(
            (el) => Number(el.id) === conf.doorHardware.doorViewerColor
        );
        const doorViewerId = conf.doorHardware && conf.doorHardware.doorViewer;
        const doorViewer = this.doorViewers.find(f => Number(f.id) === Number(doorViewerId));
        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypeOptionsPageComponent,
            resolve: {
                title: () => _('DOOR|Opcje wizjera'),
                selectedType: () => doorViewer,
                hardwareColors: () => this.hardwareColors,
                selColor: () => matchedColor,
            },
        });

        modalInstance.result.then(selectedData => {
            if (isObject(selectedData) && isDefined(selectedData.type)) {
                this.setDoorViewerColorById(selectedData.color, selectedData.defaultColor);
            }
            this.priceService.count();
            this.timeLimitService.count();
        });

        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg',
                });
            }
        });
    }

    /**
     * Funkcja ustawiająca kolor wizjera
     * @param {object} color Kolor
     */
    setDoorViewerColor(color: IccAccessoryColor | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.doorViewerColor = color ? Number(color.id) : null;
        conf.doorHardware.doorViewerColorIsDefault = color ? color.isDefault : false;
        this.emitEvent();
    }

    /**
     * Funkcja ustawiająca kolor dla wizjera
     * @param  {number} colorId  Id koloru
     */
    setDoorViewerColorById(colorId, isDefault = false) {
        const matchedColors = this.hardwareColors.filter(el => el.id === colorId);
        if (matchedColors.length > 0) {
            const color = core.copy(matchedColors[0]);
            color.isDefault = isDefault;
            this.setDoorViewerColor(color);
        } else {
            this.setDoorViewerColor(null);
        }
    }

    /**
     * Funkcja ustawiająca domyślny kolor dla wizjera
     */
    setDefaultColorForDoorViewer(conf = this.configurationsService.conf.Current) {
        const doorViewer = this.doorViewers.find(
            t => Number(t.id) === conf.doorHardware.doorViewer
        );

        if (isObject(doorViewer) && isArray(doorViewer.colors_ids) && isArray(this.hardwareColors)) {
            const categoryColor = this.findColorFromCategoryMapping(doorViewer, conf);
            if (categoryColor) {
                this.setDoorViewerColor(categoryColor, conf);
                return;
            }
            const windowColorId = this.colorMappingService.getWindowColorId(conf, 'inner');
            const matchedColors = this.colorMappingService.getColors(
                Number(windowColorId),
                'window',
                'accessory'
            );
            const colorsBasedOnColorMapping = this.colorMappingService.getColors(
                Number(windowColorId),
                'accessory',
                'accessory'
            );
            const colors = this.hardwareColors.filter(el => doorViewer.colors_ids.indexOf(el.id) > -1);
            const currentDoorViewerColor = conf.doorHardware.doorViewerColor;
            const isColorAvailable = colors?.some(c => Number(c.id) === Number(currentDoorViewerColor));

            if (isColorAvailable) {
                return;
            }

            if (isArray(colors) && isDefined(colors[0])) {
                const platesColors = conf.doorHardware.plates?.color
                    && colors.filter(c => Number(c.id) === conf.doorHardware.plates.color);
                const mappingColors =
                    colorsBasedOnColorMapping
                        .map(m => colors.filter(c => Number(c.id) === m)[0])
                        .filter(m => m) ??
                    matchedColors
                        .map(m => colors.filter(c => Number(c.id) === m)[0])
                        .filter(m => m);
                let color;

                if (isArray(platesColors) && platesColors[0]) {
                    color = core.copy(platesColors[0]);
                } else if (isArray(mappingColors) && isDefined(mappingColors[0])) {
                    color = core.copy(mappingColors[0]);
                } else {
                    color = core.copy(colors[0]);
                }
                color.isDefault = true;
                this.setDoorViewerColor(color, conf);
            }
        } else {
            this.setDoorViewerColor(null, conf);
        }
    }

    getAvailableDoorViewers(conf = this.configurationsService.conf.Current) {
        const selectedFilling = this.getSelectedFilling(conf);
        return (selectedFilling && selectedFilling.has_door_viewer) ? this.doorViewers : this.doorViewers.filter(v => v.id === 0);
    }

    getPlatesToMainLock(conf = this.configurationsService.conf.Current) {
        const plateToMainLock = (conf.Lock?.accessories || []).filter(plate =>
            (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToMainLock : this.platesToMainLock).some(cylinderToMainLock =>
                Number(cylinderToMainLock.id) === Number(plate)
            )
        );

        return (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToMainLock : this.platesToMainLock).filter(plate =>
            (plate.hardware_sets || []).includes(conf.doorHardware.set)
            && (plateToMainLock.length === 0 || plateToMainLock.map(Number).includes(Number(plate.id)))
        );
    }

    getOuterPlatesToMainLock(conf = this.configurationsService.conf.Current) {
        const innerPlate = this.innerPlatesToMainLock.find(plate => Number(plate.id) === Number(conf.doorHardware.plates.mainLock));

        return innerPlate && this.outerPlatesToMainLock.filter(plate =>
           innerPlate.outer_handles_id.map(Number).includes(Number(plate.id))
        );
    }

    getPlatesToAdditionalLock(conf = this.configurationsService.conf.Current) {
        const plateToMainLock =  (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToMainLock : this.platesToMainLock).find(
            p => castToNullableNumber(p.id) === conf.doorHardware.plates.mainLock
        );
        return  (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToAdditionalLock : this.platesToAdditionalLock).filter(
            plate =>
                plateToMainLock
                && (plate.hardware_sets || []).includes(conf.doorHardware.set)
                && plate.accessories.map(Number).includes(Number(plateToMainLock.id))
        );
    }

    getOuterPlatesToAdditionalLock(conf = this.configurationsService.conf.Current) {
        const plateToMainLock = this.outerPlatesToMainLock.find(
            p => castToNullableNumber(p.id) === conf.doorHardware.outerPlates.mainLock
        );
        const innerPlateToAdditionalPlate = this.innerPlatesToAdditionalLock.find(plate => Number(plate.id) === Number(conf.doorHardware.plates.additionalLock));

        return this.outerPlatesToAdditionalLock.filter(
            plate =>
                plateToMainLock
                && (plate.hardware_sets || []).includes(conf.doorHardware.set)
                && plate.accessories.map(Number).includes(Number(plateToMainLock.id))
                && innerPlateToAdditionalPlate && innerPlateToAdditionalPlate.outer_handles_id.map(Number).includes(Number(plate.id))
        );
    }

    getPlateKinds(conf = this.configurationsService.conf.Current) {
        return  (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToMainLock : this.platesToMainLock).reduce((plateKinds, plate) => {
            if (!plateKinds.includes(plate.plate_type)) {
                plateKinds.push(plate.plate_type);
            }
            return plateKinds;
        }, []);
    }

    getOuterPlateKinds() {
        return this.outerPlatesToMainLock.reduce((plateKinds, plate) => {
            if (!plateKinds.includes(plate.plate_type)) {
                plateKinds.push(plate.plate_type);
            }
            return plateKinds;
        }, []);
    }

    openPlateToMainLockModal(type: 'inner' | 'outer' = 'inner') {
        const conf = this.configurationsService.conf.Current;
        const matchedColor = this.hardwareColors.find(
            (el) =>
                Number(el.id) === (type === 'inner' ? conf.doorHardware.plates.color : conf.doorHardware.outerPlates.color)
        );
        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypesPageComponent,
            resolve: {
                title: () => _('DOOR|Szyldy zamka głównego'),
                types: () => type === 'outer' ? this.getOuterPlatesToMainLock() : this.getPlatesToMainLock(),
                tabs: () =>  type === 'outer' ? this.getOuterPlateKinds() : this.getPlateKinds(),
                selType: () => type === 'outer' ? conf.doorHardware.outerPlates.mainLock : conf.doorHardware.plates.mainLock,
                hardwareColors: () => this.hardwareColors,
                selColor: () => matchedColor,
                colorIdToMapping: () => this.colorIdToMapping
            },
        });

        modalInstance.result.then(selectedData => {
            if (isObject(selectedData) && isDefined(selectedData.type)) {
                if(type === 'inner') {
                    this.setPlateToMainLock(Number(selectedData.type.id));
                    this.setPlateColorById(selectedData.color, selectedData.defaultColor);
                } else {
                    this.setOuterPlateToMainLock(Number(selectedData.type.id));
                    this.setOuterPlateColorById(selectedData.color, selectedData.defaultColor);
                }
                this.eventBusService.post({ key: 'processDependencies', value: null });
                this.priceService.count();
            }
        });
    }

    /**
     * @returns {boolean} [true] - if default plate has been set
     * @returns {boolean} [false] - if default plate has been set to null
     */
    setDefaultPlateToMainLock(conf = this.configurationsService.conf.Current) {
        const plates = this.getPlatesToMainLock(conf).filter(p => this.isPlateBasedOnDependencyAvailable(p.id));

        const isDoorHardwareType = (this.hardwareTypes.length > 0 && conf.doorHardware.type);
        let isPlateSet = false;
        if (plates.length > 0 && conf.doorHardware.set) {
            if (isDoorHardwareType) {
                const type = this.getTypes(conf).find(
                    t => Number(t.id) === Number(conf.doorHardware.type)
                );
                if (type) {
                    let defaultPlateId;
                    defaultPlateId = type.plate_main_lock_lever_id;
                    if (conf.doorHardware.set === 'leverKnob' && type.plate_main_lock_knob_id) {
                        defaultPlateId = type.plate_main_lock_knob_id;
                    } else if (
                        conf.doorHardware.set === 'leverPull'
                        && type.plate_main_lock_pull_id
                    ) {
                        defaultPlateId = type.plate_main_lock_pull_id;
                    }
                    if (defaultPlateId && plates.map(p => castToNullableNumber(p.id)).includes(Number(defaultPlateId))) {
                        this.setPlateToMainLock(Number(defaultPlateId));
                        isPlateSet = true;
                    }
                }
            } else if (this.hardwareTypes.length === 0) {
                if (!plates.map(p => castToNullableNumber(p.id)).includes(conf.doorHardware.plates.mainLock)) {
                    this.setPlateToMainLock(Number(plates[0].id));
                    this.setDefaultColorForPlate(conf);
                    isPlateSet = true;
                } else if (
                    conf.doorHardware.plates.mainLock
                    && plates.map(p => castToNullableNumber(p.id)).includes(conf.doorHardware.plates.mainLock)
                ) {
                    isPlateSet = true;
                }
            }
        }
        if (!isPlateSet) {
            this.setPlateToMainLock(null);
            this.setDefaultColorForPlate(conf);
        }
    }

    setPlateToMainLock(id: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.plates.mainLock = id;
        this.setDefaultOuterPlateToMainLock();
        this.setDefaultCylinderToMainLock(conf);
        this.setDefaultPlateToAdditionalLock(conf);
        this.setDefaultOuterPlateToAdditionalLock(conf);
        this.eventBusService.post({ key: 'processDependencies', value: null });
        this.emitEvent();
    }

    setDefaultOuterPlateToMainLock(conf = this.configurationsService.conf.Current) {
        if (conf.doorHardware.set !== 'mixedLever') {
            this.setOuterPlateToMainLock(null);
            this.setOuterPlateColor(null, conf);
            return;
        }

        const innerPlate = this.innerPlatesToMainLock.find(plate =>
            Number(plate.id) === Number(conf.doorHardware.plates.mainLock)
        );

        if (!innerPlate) {
            this.setOuterPlateToMainLock(null);
            this.setOuterPlateColor(null, conf);
            return;
        }

        const currentOuterPlateId = conf.doorHardware.outerPlates.mainLock;
        const isCurrentOuterPlateValid = currentOuterPlateId &&
            innerPlate.outer_handles_id.map(Number).includes(Number(currentOuterPlateId));

        if (!isCurrentOuterPlateValid) {
            this.setOuterPlateToMainLock(Number(innerPlate.outer_handles_id[0]) || null);
        }
        this.setDefaultColorForOuterPlate(conf);
    }

    setOuterPlateToMainLock(id: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.outerPlates.mainLock = id;
        this.eventBusService.post({ key: 'processDependencies', value: null });
        this.emitEvent();
    }

    /**
     * Okno modalne z wyborem klamki
     * @param  {object} sash      Skrzydło
     * @param  {string} place     Położenie
     * @param  {string} type      Typ
     */
    openModalPlateOptions(type: 'inner' | 'outer' = 'inner') {
        const conf = this.configurationsService.conf.Current;
        const matchedColor = this.hardwareColors.find(
            (el) =>
                Number(el.id) === (type === 'inner' ? conf.doorHardware.plates.color : conf.doorHardware.outerPlates.color)
        );
        const plateId = conf.doorHardware && conf.doorHardware.plates.mainLock;
        const plate =  (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToMainLock : this.platesToMainLock).find(f => Number(f.id) === Number(plateId));
        const outerPlateId = conf.doorHardware && conf.doorHardware.outerPlates.mainLock;
        const outerPlate =  this.outerPlatesToMainLock.find(f => Number(f.id) === Number(outerPlateId));
        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypeOptionsPageComponent,
            resolve: {
                title: _('WINDOW|Opcje szyldu zamka głównego'),
                selectedType: () => type === 'inner' ? plate : outerPlate,
                hardwareColors: () => this.hardwareColors,
                selColor: () => matchedColor,
            },
        });

        modalInstance.result.then(selectedData => {
            if (isObject(selectedData) && isDefined(selectedData.type)) {
                if(type === 'inner') {
                    this.setPlateColorById(selectedData.color, selectedData.defaultColor);
                } else {
                    this.setOuterPlateColorById(selectedData.color, selectedData.defaultColor);
                }
            }
            this.priceService.count();
            this.timeLimitService.count();
        });
        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg',
                });
            }
        });
    }

    openModalPullOptions() {
        const matchedColor = this.hardwareColors.find(
            (el) => Number(el.id) === this.configurationsService.conf.Current.doorHardware.pullColor
        );
        const pullId = this.configurationsService.conf.Current.doorHardware
            && this.configurationsService.conf.Current.doorHardware.pull;
        const pull = this.pulls.find(f => Number(f.id) === Number(pullId));

        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypeOptionsPageComponent,
            resolve: {
                title: _('WINDOW|Opcje pochwytu'),
                selectedType: () => pull,
                hardwareColors: () => this.hardwareColors,
                selColor: () => matchedColor,
            },
        });

        modalInstance.result.then(selectedData => {
            if (isObject(selectedData) && isDefined(selectedData.type)) {
                this.setPullColorById(selectedData.color, selectedData.defaultColor);
            }
            this.priceService.count();
            this.timeLimitService.count();
        });
        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg',
                });
            }
        });
    }

    openRecessedPullsModal() {
        const matchedColor = this.hardwareColors.find(el => (Number(el.id) === this.configurationsService.conf.Current.doorHardware.pullColor));
        const recessedPulls = this.getRecessedPulls();

        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypesPageComponent,
            resolve: {
                title: () => _('DOOR|Pochwyty kieszeniowe'),
                types: () => recessedPulls,
                tabs: () => this.getTabsBasedOnWindowAccessoriesCategoryId(recessedPulls),
                selType: () => this.configurationsService.conf.Current.doorHardware.recessedPull,
                selColor: () => matchedColor,
                canOpenOptions: () => (type: IccAccessoryAccessory) => false,
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected.type)) {
                this.setRecessedPull(Number(selected.type.id))
                this.openModalRecessedPullOptions();
            }
        });
    }

    openModalRecessedPullOptions() {
        const recessedPullId = this.configurationsService.conf.Current.doorHardware
            && this.configurationsService.conf.Current.doorHardware.recessedPull;
        const recessedPull = this.recessedPulls.find(f => Number(f.id) === Number(recessedPullId));

        const modalInstance = this.modalService.open({
            pageComponent: RecessedPullOptionsPageComponent,
            resolve: {
                title: _('DOOR|Opcje pochwytu kieszeniowego'),
                selectedType: () => recessedPull,
                recessedPullColors: () => this.getRecessedPullColors(),
                selColor: () => this.configurationsService.conf.Current.doorHardware.recessedPullColor,
            },
        });

        modalInstance.result.then(selectedData => {
            if (isObject(selectedData) && isDefined(selectedData.type)) {
                this.setColorForRecessedPull(selectedData.color.colorSource, selectedData.color.color, false, selectedData.color.price, selectedData.color.list_price)
            }
            this.priceService.count();
            this.timeLimitService.count();
        });
        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg',
                });
            }
        });
    }

    setPullColor(color: IccAccessoryColor, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.pullColor = (color && Number(color.id)) || null;
        conf.doorHardware.pullColorIsDefault = color?.isDefault ?? false;
        if (color?.id) {
            this.setColorsBasedOnColorMapping(Number(color.id), 'pull');
        }
        this.emitEvent();
    }

    setPullColorById(colorId, isDefault = false) {
        const matchedColors = this.hardwareColors.filter(el => Number(el.id) === Number(colorId));
        if (matchedColors.length > 0) {
            const color = core.copy(matchedColors[0]);
            color.isDefault = isDefault;
            this.setPullColor(color);
        }
    }

    setDefaultColorForPull(conf = this.configurationsService.conf.Current) {
        const pull = this.pulls.find(t => Number(t.id) === conf.doorHardware.pull)

        if (isObject(pull) && isArray(pull.colors_ids) && isArray(this.hardwareColors)) {
            const categoryColor = this.findColorFromCategoryMapping(pull, conf);
            if (categoryColor) {
                this.setPullColor(categoryColor, conf);
                return;
            }
            const windowColorId = this.colorMappingService.getWindowColorId(conf, 'inner');
            const matchedColors = this.colorMappingService.getColors(
                Number(windowColorId),
                'window',
                'accessory'
            );
            const colors = this.hardwareColors.filter(el => pull.colors_ids.indexOf(el.id) > -1);
            const pullColor = conf.doorHardware.pullColor;
            const isColorAvailable = colors?.some(c => Number(c.id) === Number(pullColor));
            if (isColorAvailable) {
                return;
            }
            if (isArray(colors) && isDefined(colors[0])) {
                const plateColors = pull.pull_color_related_to_plate
                    && conf.doorHardware.plates?.color
                    && colors.filter(c => Number(c.id) === conf.doorHardware.plates.color);
                const windowColors = matchedColors
                    .map(m => colors.filter(c => Number(c.id) === m)[0])
                    .filter(m => m);
                let color;
                if (isArray(plateColors) && plateColors[0]) {
                    color = core.copy(plateColors[0]);
                } else if (isArray(windowColors) && isDefined(windowColors[0])) {
                    color = core.copy(windowColors[0]);
                } else {
                    color = core.copy(colors[0]);
                }
                color.isDefault = true;
                this.setPullColor(color, conf);
            }
        } else {
            this.setPullColor(null, conf);
        }
    }

    setDefaultColorForMainCylinder(conf) {
        const cylinder = this.cylindersToMainLock.find(t => Number(t.id) === conf.doorHardware.cylinders.mainLock)
        const cylinderMainLockColors = this.cylindersToMainLock.find(
            (p) => Number(p.id) === Number(conf.doorHardware.cylinders.mainLock)
        )?.colors_ids;
        const selectedMainLockColor = conf.doorHardware.cylinders.mainLockColor || conf.doorHardware.cylinders.color;

        if (isObject(cylinder) && isArray(cylinder.colors_ids) && isArray(this.hardwareColors)) {
            const categoryColor = this.findColorFromCategoryMapping(cylinder, conf);
            if (categoryColor) {
                this.setCylinderMainLockColor(categoryColor, conf);
                return;
            }

            const isColorAvailable = cylinderMainLockColors?.some((el) => Number(el) === Number(selectedMainLockColor));
            if (isColorAvailable) {
                return;
            }
            const windowColorId = this.colorMappingService.getWindowColorId(conf, 'inner');
            const matchedColors = this.colorMappingService.getColors(
                Number(windowColorId),
                'window',
                'accessory'
            );
            const colorsBasedOnColorMapping = this.colorMappingService.getColors(
                Number(windowColorId),
                'accessory',
                'accessory'
            );
            const colors = this.hardwareColors.filter(el => cylinder.colors_ids.indexOf(el.id) > -1);
            if (isArray(colors) && isDefined(colors[0])) {
                const mappingColors =
                    colorsBasedOnColorMapping
                        .map(m => colors.filter(c => Number(c.id) === m)[0])
                        .filter(m => m) ??
                    matchedColors
                        .map(m => colors.filter(c => Number(c.id) === m)[0])
                        .filter(m => m);
                let color;

                if (isArray(mappingColors) && isDefined(mappingColors[0])) {
                    color = core.copy(mappingColors[0]);
                } else {
                    color = core.copy(colors[0]);
                }
                color.isDefault = true;
                this.setCylinderMainLockColor(color, conf);
            }
        } else {
            this.setCylinderMainLockColor(null, conf);
        }
    }

    setCylinderMainLockColor(color: IccAccessoryColor, conf = this.configurationsService.conf.Current) {
        const colorId = (color && Number(color.id)) || null;
        conf.doorHardware.cylinders.mainLockColor = colorId;
        conf.doorHardware.cylinders.color = colorId;
        conf.doorHardware.cylinders.mainLockColorIsDefault = color?.isDefault ?? false;
        this.emitEvent();
    }

    setDefaultColorForAdditionalCylinder(conf) {
        const cylinder = this.cylindersToAdditionalLock.find(
            t => Number(t.id) === Number(conf.doorHardware.cylinders.additionalLock)
        );
        const additionalCylinderColors = cylinder?.colors_ids;
        const isColorAvailable = additionalCylinderColors?.some(
            (el) => Number(el) === Number(conf.doorHardware.cylinders.additionalLockColor)
        );
        const isRelatedToMain = cylinder.additional_cylinder_related_to_main !== false;

        if (!isRelatedToMain && isColorAvailable && !conf.doorHardware.cylinders.additionalLockColorIsDefault) {
            return;
        }

        if (!isObject(cylinder) || !isArray(cylinder.colors_ids) || !isArray(this.hardwareColors)) {
            this.setCylinderAdditionalLockColor(null, conf, true);
            return;
        }


        if (isRelatedToMain) {
            const mainLockColorId = conf.doorHardware.cylinders.mainLockColor
                ?? conf.doorHardware.cylinders.color;
            const colors = this.hardwareColors.filter(el => cylinder.colors_ids.indexOf(el.id) > -1);

            let targetColor = mainLockColorId != null
                ? colors.find(c => Number(c.id) === Number(mainLockColorId))
                : undefined;

            if (!targetColor && mainLockColorId != null) {
                const categoryColor = this.findColorFromCategoryMapping(cylinder, conf);
                if (categoryColor) {
                    this.setCylinderAdditionalLockColor(categoryColor, conf);
                    return;
                }
                const mappedColorIds = this.colorMappingService.getMappedColorsFromAccessory(Number(mainLockColorId));
                const matchedColorIds = mappedColorIds?.filter(el =>
                    additionalCylinderColors?.some((p: number) => Number(p) === Number(el))
                );
                targetColor = matchedColorIds
                    ?.map(m => colors.find(c => Number(c.id) === m))
                    .find(Boolean);
            }

            if (targetColor) {
                const colorToSet = core.copy(targetColor);
                colorToSet.isDefault = true;
                this.setCylinderAdditionalLockColor(colorToSet, conf, true);
            } else {
                this.setCylinderAdditionalLockColor(null, conf, true);
            }
            return;
        }

        const cylinderColorId = conf.doorHardware.cylinders.color;
        const mappedColorIds = this.colorMappingService.getMappedColorsFromAccessory(Number(cylinderColorId));
        const matchedColorIds = mappedColorIds?.filter((el) =>
            additionalCylinderColors?.some((p) => Number(p) === Number(el))
        );

        const colors = this.hardwareColors.filter(el => cylinder.colors_ids.indexOf(el.id) > -1);
        const mappedColor = matchedColorIds
            ?.map(m => colors.find(c => Number(c.id) === m))
            .find(Boolean);

        const color = core.copy(mappedColor ?? colors[0]);
        if (color) {
            color.isDefault = true;
            this.setCylinderAdditionalLockColor(color, conf, true);
        } else {
            this.setCylinderAdditionalLockColor(null, conf, true);
        }
    }

    setCylinderAdditionalLockColor(
        color: IccAccessoryColor,
        conf = this.configurationsService.conf.Current,
        isDefault = false
    ) {
        conf.doorHardware.cylinders.additionalLockColor = (color && Number(color.id)) || null;
        conf.doorHardware.cylinders.additionalLockColorIsDefault = (color && Number(color.id)) ? isDefault : true;
        this.emitEvent();
    }

    /**
     * Funkcja ustawiajca kolor szyldu
     * @param {object} color Kolor
     */
    setPlateColor(color: IccAccessoryColor, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.plates.color = (color && Number(color.id)) || null;
        conf.doorHardware.plates.colorIsDefault = color?.isDefault ?? false;
        if (this.configurationsService.conf.Current.doorHardware.pullColorRelatedToPlate) {
            this.setDefaultColorForPull(conf);
        }
        if (conf.doorHardware.decorativeHingeCoversColorRelatedToPlate && conf.doorHardware.plates?.color) {
            this.setDecorativeHingeCoverColorById(conf.doorHardware.plates.color, true);
        }

        this.accessoriesService.setAccessoriesColorBasedOnHardware('plates');
        this.setColorsBasedOnColorMapping(conf.doorHardware.plates.color, 'plates')
        this.emitEvent();
    }

    setOuterPlateColor(color: IccAccessoryColor, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.outerPlates.color = (color && Number(color.id)) || null;
        conf.doorHardware.outerPlates.colorIsDefault = color?.isDefault ?? false;
        this.setColorsBasedOnColorMapping(conf.doorHardware.plates.color, 'outerPlates')
        this.emitEvent();
    }

    /**
     * Funkcja ustawiająca domyslny kolor dla szyldów
     */
    setDefaultColorForPlate(conf = this.configurationsService.conf.Current) {
        const plate =  (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToMainLock : this.platesToMainLock).find(
            t => Number(t.id) === conf.doorHardware.plates.mainLock
        );

        if (isObject(plate) && isArray(plate.colors_ids) && isArray(this.hardwareColors)) {
            const categoryColor = this.findColorFromCategoryMapping(plate, conf);
            if (categoryColor) {
                this.setPlateColor(categoryColor, conf);
                return;
            }
            const windowColorId = this.colorMappingService.getWindowColorId(conf, 'inner');
            const matchedColors = this.colorMappingService.getColors(
                Number(windowColorId),
                'window',
                'accessory'
            );
            const colors = this.hardwareColors.filter(el => plate.colors_ids.indexOf(el.id) > -1);
            const currentPlateColor = conf.doorHardware.plates.color;
            const currentPlateColorInColors = colors.some(colorId => Number(colorId.id) === Number(currentPlateColor));
            if (isArray(colors) && isDefined(colors[0]) && !currentPlateColorInColors) {
                const typeColors = conf.doorHardware.type && conf.doorHardware.color && colors.filter(c => Number(c.id) === conf.doorHardware.color);
                const windowColors = matchedColors
                    .map(m => colors.filter(c => Number(c.id) === m)[0])
                    .filter(m => m);
                let color;
                if (isArray(typeColors) && typeColors[0]) {
                    color = core.copy(typeColors[0]);
                } else if (isArray(windowColors) && isDefined(windowColors[0])) {
                    color = core.copy(windowColors[0]);
                } else {
                    color = core.copy(colors[0]);
                }
                color.isDefault = true;
                this.setPlateColor(color, conf);
            }
        } else {
            this.setPlateColor(null, conf);
        }
    }

    setDefaultColorForOuterPlate(conf = this.configurationsService.conf.Current) {
        const plate = this.outerPlatesToMainLock.find(
            t => Number(t.id) === conf.doorHardware.outerPlates.mainLock
        );

        if (isObject(plate) && isArray(plate.colors_ids) && isArray(this.hardwareColors)) {
            const categoryColor = this.findColorFromCategoryMapping(plate, conf);
            if (categoryColor) {
                this.setOuterPlateColor(categoryColor, conf);
                return;
            }
            const windowColorId = this.colorMappingService.getWindowColorId(conf, 'inner');
            const matchedColors = this.colorMappingService.getColors(
                Number(windowColorId),
                'window',
                'accessory'
            );
            const colors = this.hardwareColors.filter(el => plate.colors_ids.indexOf(el.id) > -1);
            const currentPlateColor = conf.doorHardware.outerPlates.color;
            const currentPlateColorInColors = colors.some(colorId => Number(colorId.id) === Number(currentPlateColor));
            if (isArray(colors) && isDefined(colors[0]) && !currentPlateColorInColors) {
                const typeColors = conf.doorHardware.type && conf.doorHardware.color && colors.filter(c => Number(c.id) === conf.doorHardware.color);
                const windowColors = matchedColors
                    .map(m => colors.filter(c => Number(c.id) === m)[0])
                    .filter(m => m);
                let color;
                if (isArray(typeColors) && typeColors[0]) {
                    color = core.copy(typeColors[0]);
                } else if (isArray(windowColors) && isDefined(windowColors[0])) {
                    color = core.copy(windowColors[0]);
                } else {
                    color = core.copy(colors[0]);
                }
                color.isDefault = true;
                this.setOuterPlateColor(color, conf);
            }
        } else {
            this.setOuterPlateColor(null, conf);
        }
    }

    /**
     * Funkcja ustawiająca kolor dla szyldu
     * @param  {number} colorId  Id koloru
     */
    setPlateColorById(colorId, isDefault = false) {
        const matchedColors = this.hardwareColors.filter(el => el.id === colorId);
        if (matchedColors.length > 0) {
            const color = core.copy(matchedColors[0]);
            color.isDefault = isDefault;
            this.setPlateColor(color);
        }
    }

    setOuterPlateColorById(colorId, isDefault = false) {
        const matchedColors = this.hardwareColors.filter(el => el.id === colorId);
        if (matchedColors.length > 0) {
            const color = core.copy(matchedColors[0]);
            color.isDefault = isDefault;
            this.setOuterPlateColor(color);
        }
    }

    setDefaultColorForElectronicLock(conf = this.configurationsService.conf.Current) {
        const electronicLock = this.electronicLocks.find(f => Number(f.id) === Number(conf.doorHardware.electronicLock?.id));

        if (isObject(electronicLock) && isArray(electronicLock.colors_ids) && isArray(this.hardwareColors)) {
            const categoryColor = this.findColorFromCategoryMapping(electronicLock, conf);
            if (categoryColor) {
                this.setElectronicLockColor(categoryColor, conf);
                return;
            }
            const mainLockCylinderColor = conf.doorHardware.cylinders.mainLockColor || conf.doorHardware.cylinders.color;
            const matchedColors = this.colorMappingService.getColors(
                Number(mainLockCylinderColor),
                'window',
                'accessory'
            );
            const colorsBasedOnColorMapping = this.colorMappingService.getColors(
                Number(mainLockCylinderColor),
                'accessory',
                'accessory'
            );
            const colors = this.hardwareColors.filter(el => electronicLock.colors_ids.indexOf(el.id) > -1);
            if (isArray(colors) && isDefined(colors[0])) {
                const mappingColors =
                    colorsBasedOnColorMapping
                        .map(m => colors.filter(c => Number(c.id) === m)[0])
                        .filter(m => m) ??
                    matchedColors
                        .map(m => colors.filter(c => Number(c.id) === m)[0])
                        .filter(m => m);
                let color;

                if (isArray(mappingColors) && isDefined(mappingColors[0])) {
                    color = core.copy(mappingColors[0]);
                } else {
                    color = core.copy(colors[0]);
                }
                color.isDefault = true;
                this.setElectronicLockColor(color, conf);
            }
        } else {
            this.setElectronicLockColor(null, conf);
        }
    }


    private getImageUrl(hardware: IccAccessoryAccessory, selectedColor: string): string {
        return hardware && (hardware.colors_imgs && hardware.colors_imgs[selectedColor]
            ? '/files/windowhandlescolorswindowaccessory/' + hardware.colors_imgs[selectedColor]
            : '/files/windowaccessory/' + hardware.img);
    }

    plateAdditionalLockBoxSelect = (conf: DoorActiveConfiguration) => {
        const plate = (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToAdditionalLock : this.platesToAdditionalLock).find(
            t => Number(t.id) === conf.doorHardware.plates.additionalLock
        );
        const matchedColor = this.hardwareColors.find(el =>  Number(el.id) === conf.doorHardware.plates.color);
        const name = plate ? plate.name : '';
        const color = matchedColor ? matchedColor.name : null;
        const colorId = matchedColor ? matchedColor.id : null;
        const description = plate ? plate.description : '';
        const imagePath = this.getImageUrl(plate, colorId);
        return {
            imagePath,
            name,
            color,
            colorId,
            description,
        };
    }

    outerPlateAdditionalLockBoxSelect = (conf: DoorActiveConfiguration) => {
        const plate = (conf.doorHardware.set === 'mixedLever' ? this.outerPlatesToAdditionalLock : this.platesToAdditionalLock).find(
            t => Number(t.id) === conf.doorHardware.outerPlates.additionalLock
        );
        const matchedColor = this.hardwareColors.find(el =>  Number(el.id) === conf.doorHardware.outerPlates.color);
        const name = plate ? plate.name : '';
        const color = matchedColor ? matchedColor.name : null;
        const colorId = matchedColor ? matchedColor.id : null;
        const description = plate ? plate.description : '';
        const imagePath = this.getImageUrl(plate, colorId);
        return {
            imagePath,
            name,
            color,
            colorId,
            description,
        };
    }

    plateMainLockBoxSelect = (conf: DoorActiveConfiguration) => {
        const plate = (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToMainLock : this.platesToMainLock).find(
            t => Number(t.id) === conf.doorHardware.plates.mainLock
        );
        const matchedColor = this.hardwareColors.find(el =>  Number(el.id) === conf.doorHardware.plates.color);
        const name = plate ? plate.name : '';
        const color = matchedColor ? matchedColor.name : null;
        const colorId = matchedColor ? matchedColor.id : null;
        const description = plate ? plate.description : '';
        const imagePath = this.getImageUrl(plate, colorId);
        return {
            imagePath,
            name,
            color,
            colorId,
            description,
        };
    };

    outerPlateMainLockBoxSelect = (conf: DoorActiveConfiguration) => {
        const plate = this.outerPlatesToMainLock.find(
            t => Number(t.id) === conf.doorHardware.outerPlates.mainLock
        );
        const matchedColor = this.hardwareColors.find(el =>  Number(el.id) === conf.doorHardware.outerPlates.color);
        const name = plate ? plate.name : '';
        const color = matchedColor ? matchedColor.name : null;
        const colorId = matchedColor ? matchedColor.id : null;
        const description = plate ? plate.description : '';
        const imagePath = this.getImageUrl(plate, colorId);
        return {
            imagePath,
            name,
            color,
            colorId,
            description,
        };
    };

    cylinderMainLockBoxSelect = (conf: DoorActiveConfiguration) => {
        const cylinder = this.cylindersToMainLock.find(
            t => Number(t.id) === conf.doorHardware.cylinders.mainLock
        );
        const selectedMainLockColor = conf.doorHardware.cylinders.mainLockColor || conf.doorHardware.cylinders.color;
        const matchedColor = this.hardwareColors.find(el =>  Number(el.id) === selectedMainLockColor);
        const name = cylinder ? cylinder.name : '';
        const color = matchedColor ? matchedColor.name : null;
        const colorId = matchedColor ? matchedColor.id : null;
        const description = cylinder ? cylinder.description : '';
        const imagePath = this.getImageUrl(cylinder, colorId);
        return {
            imagePath,
            name,
            color,
            colorId,
            description,
        };
    };

    cylinderAdditionalLockBoxSelect = (conf: DoorActiveConfiguration) => {
        const cylinder = this.cylindersToAdditionalLock.find(
            t => Number(t.id) === conf.doorHardware.cylinders.additionalLock
        );
        const matchedColor = this.hardwareColors.find(
            el =>  Number(el.id) === Number(conf.doorHardware.cylinders.additionalLockColor)
        );
        const name = cylinder ? cylinder.name : '';
        const color = matchedColor ? matchedColor.name : null;
        const colorId = matchedColor ? matchedColor.id : null;
        const description = cylinder ? cylinder.description : '';
        const imagePath = this.getImageUrl(cylinder, colorId);
        return {
            imagePath,
            name,
            color,
            colorId,
            description,
        };
    };

    hingeBoxSelect = (conf: DoorActiveConfiguration) => {
        if (!conf.Hinge?.id) {
            return;
        }
        const cylinder = this.hinges.find(
            t => Number(t.id) === Number(conf.Hinge.id)
        );
        const matchedColor = this.hardwareColors.find(el =>  Number(el.id) === Number(conf.HingeColor?.id));
        const colorId = matchedColor ? matchedColor.id : null;
        const imagePath = this.getImageUrl(cylinder, colorId);
        return {
            imagePath,
        };
    }

    openPlateToAdditionalLockModal(conf = this.configurationsService.conf.Current) {
        const modalInstance = this.modalService.open({
            pageComponent: ListPageComponent,
            resolve: {
                title: () => _('DOOR|Szyldy zamka dodatkowego'),
                selectedId: () => conf.doorHardware.plates.additionalLock,
                items: () => this.getPlatesToAdditionalLock(conf),
                imageUrl: () => '/files/windowaccessory',
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected)) {
                this.setPlateToAdditionalLock(Number(selected.id));
            }
        });
    }

    openOuterPlateToAdditionalLockModal(conf = this.configurationsService.conf.Current) {
        const modalInstance = this.modalService.open({
            pageComponent: ListPageComponent,
            resolve: {
                title: () => _('DOOR|Szyldy zamka dodatkowego zewnętrznego'),
                selectedId: () => conf.doorHardware.outerPlates.additionalLock,
                items: () => this.getOuterPlatesToAdditionalLock(conf),
                imageUrl: () => '/files/windowaccessory',
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected)) {
                this.setOuterPlateToAdditionalLock(Number(selected.id));
            }
        });
    }

    setDefaultPlateToAdditionalLock(conf = this.configurationsService.conf.Current) {
        const plates = this.getPlatesToAdditionalLock(conf);
        if (plates.length > 0) {
            if (this.hardwareTypes.length > 0 && conf.doorHardware.type) {
                const type = this.getTypes(conf).find(
                    t => Number(t.id) === Number(conf.doorHardware.type)
                );
                if (
                    type
                    && type.plate_additional_lock_id
                    && plates.map(p => castToNullableNumber(p.id)).includes(Number(type.plate_additional_lock_id))
                ) {
                    this.setPlateToAdditionalLock(Number(type.plate_additional_lock_id));
                }
            }
            if (!plates.map(p => castToNullableNumber(p.id)).includes(conf.doorHardware.plates.additionalLock)) {
                this.setPlateToAdditionalLock(Number(plates[0].id));
            }
        } else {
            this.setPlateToAdditionalLock(null);
        }
    }

    setPlateToAdditionalLock(id: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.plates.additionalLock = id;
        this.setDefaultCylinderToAdditionalLock(conf);
        this.setDefaultOuterPlateToAdditionalLock(conf);
        this.eventBusService.post({ key: 'processDependencies', value: null });
        this.emitEvent();
    }

    setDefaultOuterPlateToAdditionalLock(conf = this.configurationsService.conf.Current) {
        const plates = this.getOuterPlatesToAdditionalLock(conf);
        if (plates.length > 0) {
            const correctOuterPlateToAdditionalLock = plates.some(plate => Number(plate.id) === Number(conf.doorHardware.outerPlates.additionalLock));
            if(!correctOuterPlateToAdditionalLock) {
                this.setOuterPlateToAdditionalLock(Number(plates[0].id));

            }
        } else {
            this.setOuterPlateToAdditionalLock(null);
        }
    }

    setOuterPlateToAdditionalLock(id: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.outerPlates.additionalLock = id;
        this.eventBusService.post({ key: 'processDependencies', value: null });
        this.emitEvent();
    }

    openCylinderToMainLockModal(conf = this.configurationsService.conf.Current) {
        const selectedMainLockColor = conf.doorHardware.cylinders.mainLockColor || conf.doorHardware.cylinders.color;
        const matchedColor = this.hardwareColors.find(
            (el) =>
                Number(el.id) === Number(selectedMainLockColor)
        );

        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypesPageComponent,
            resolve: {
                title: () => _('DOOR|Rodzaje wkładek zamka głównego'),
                types: () => this.getCylindersToMainLock(conf),
                selType: () => conf.doorHardware.cylinders.mainLock,
                selColor: () => matchedColor,
                hardwareColors: () => this.hardwareColors,
                canOpenOptions: () => conf.doorHardware.cylinders.isKeyAvailableForMainLock,
                colorIdToMapping: () => this.colorIdToMapping
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected.type)) {
                this.setCylinderToMainLock(Number(selected.type.id));
                if (selected?.color) {
                    this.setMainLockColorById(selected.color, selected.defaultColor);
                }
                this.eventBusService.post({ key: 'processDependencies', value: null });
                this.priceService.count();
            }
        });
    }

    /**
     * @returns {boolean} [true] - if default cylinder has been set or has not changed
     * @returns {boolean} [false] - if default cylinder has been set to null
     */
    setDefaultCylinderToMainLock(conf = this.configurationsService.conf.Current): boolean {
        const cylinders = this.getCylindersToMainLock(conf).filter(c => this.isCylinderBasedOnDependencyAvailable(c.id));
        if (cylinders.length > 0 && conf.doorHardware.set) {
            if (this.hardwareTypes.length === 0) {
                if (!cylinders.map(p => castToNullableNumber(p.id)).includes(conf.doorHardware.cylinders.mainLock)) {
                    this.setCylinderToMainLock(Number(cylinders[0].id));
                }
                return true;
            } else if (
                this.hardwareTypes.length > 0
                && conf.doorHardware.type
            ) {
                const type = this.getTypes(conf).find(
                    t => Number(t.id) === Number(conf.doorHardware.type)
                );
                if (
                    type
                    && type.cylinder_main_lock_id
                    && cylinders.map(p => castToNullableNumber(p.id)).includes(Number(type.cylinder_main_lock_id))
                ) {
                    this.setCylinderToMainLock(Number(type.cylinder_main_lock_id));
                    return true;
                }
            }
        }
        this.setCylinderToMainLock(null);
        return false;
    }

    setCylinderToMainLock(id: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.cylinders.mainLock = id;
        if(id) {
            this.setDefaultColorForMainCylinder(conf);
        }
        this.setDefaultCylinderToAdditionalLock(conf);
        this.setDefaultCylinderSystem(conf);
        this.setDefaultElectronicLock(conf);
        this.setKeyOptionsForMainLock(conf);
        this.emitEvent();
    }

    setKeyOptionsForMainLock(conf = this.configurationsService.conf.Current) {
        const cylinder = this.cylindersToMainLock.find(
            c => Number(c.id) === Number(conf.doorHardware.cylinders.mainLock)
        );
        if (this.cylindersToMainLock.length > 0 && cylinder && cylinder.max_key_quantity > 0) {
            this.setDefaultMaxKeyQuantityForMainLock(Number(cylinder.max_key_quantity));
            this.setDefaultKeyPriceForMainLock(Number(cylinder.key_price));

            if (cylinder.additional_key_for_the_cylinder && !cylinder.fits_the_lock) {
                conf.doorHardware.cylinders.isKeyAvailableForMainLock = cylinder.additional_key_for_the_cylinder;
            }
        } else {
            conf.doorHardware.cylinders.isKeyAvailableForMainLock = false;
            conf.doorHardware.cylinders.keyQuantityForMainLock = 0;
        }
    }

    setDefaultMaxKeyQuantityForMainLock(quantity: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.cylinders.maxKeyQuantityForMainLock = quantity;
    }

    setDefaultKeyPriceForMainLock(price: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.cylinders.keyPriceForMainLock = price;
    }

    setKeyOptionsForAdditionalLock(conf) {
        const cylinder = this.cylindersToAdditionalLock.find(
            c => Number(c.id) === Number(conf.doorHardware.cylinders.additionalLock)
        );
        if(this.cylindersToAdditionalLock.length > 0 && cylinder && cylinder.additional_key_for_the_cylinder) {
            this.setDefaultMaxKeyQuantityForAdditionalLock(Number(cylinder.max_key_quantity));
            this.setDefaultKeyPriceForAdditionalLock(Number(cylinder.key_price));
            conf.doorHardware.cylinders.isKeyAvailableForAdditionalLock = true;
        } else {
            conf.doorHardware.cylinders.isKeyAvailableForAdditionalLock = false;
            conf.doorHardware.cylinders.keyQuantityForAdditionalLock = 0;
        }
    }

    setDefaultMaxKeyQuantityForAdditionalLock(quantity: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.cylinders.maxKeyQuantityForMainLock = quantity;
    }

    setDefaultKeyPriceForAdditionalLock(price: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.cylinders.keyPriceForAdditionalLock = price;
    }

    getCylindersToMainLock(conf = this.configurationsService.conf.Current) {
        const plateToMainLock = (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToMainLock : this.platesToMainLock).find(
            p => castToNullableNumber(p.id) === conf.doorHardware.plates.mainLock
        );
        const cylindersToLock = (conf.Lock?.accessories || []).filter(cylinder =>
            this.cylindersToMainLock.some(cylinderToMainLock =>
                Number(cylinderToMainLock.id) === Number(cylinder)
            )
        );
        return this.cylindersToMainLock.filter(
            cylinder =>
                plateToMainLock
                && plateToMainLock.accessories.map(Number).includes(Number(cylinder.id))
                && (cylindersToLock.length === 0 || cylindersToLock.map(Number).includes(Number(cylinder.id)))
        );
    }

    getCylindersToAdditionalLock(conf = this.configurationsService.conf.Current) {
        const cylinderToMainLock = this.cylindersToMainLock.find(
            p => castToNullableNumber(p.id) === conf.doorHardware.cylinders.mainLock
        );
        const plateToAdditionalLock = (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToAdditionalLock : this.platesToAdditionalLock).find(
            p => castToNullableNumber(p.id) === conf.doorHardware.plates.additionalLock
        );

        return this.cylindersToAdditionalLock.filter(
            cylinder =>
                cylinderToMainLock
                && plateToAdditionalLock
                && cylinder.accessories.map(Number).includes(Number(cylinderToMainLock.id))
                && plateToAdditionalLock.accessories.map(Number).includes(Number(cylinder.id))
        );
    }

    openCylinderToAdditionalLockModal(conf = this.configurationsService.conf.Current) {
        const selectedAdditionalLockColor = this.hardwareColors.find(
            (el) => Number(el.id) === Number(conf.doorHardware.cylinders.additionalLockColor)
        );
        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypesPageComponent,
            resolve: {
                title: () => _('DOOR|Rodzaje wkładek zamka dodatkowego'),
                types: () => this.getCylindersToAdditionalLock(),
                selType: () => conf.doorHardware.cylinders.additionalLock,
                hardwareColors: () => this.hardwareColors,
                selColor: () => selectedAdditionalLockColor,
                colorIdToMapping: () => this.colorIdToMapping,
                canOpenOptions: () => (type: IccAccessoryAccessory) =>
                    type.additional_cylinder_related_to_main === false
                    && (type.colors_ids?.length ?? 0) > 1,
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected.type)) {
                this.setCylinderToAdditionalLock(Number(selected.type.id));
                if (selected?.color) {
                    this.setAdditionalLockColorById(selected.color, selected.defaultColor);
                }
                this.eventBusService.post({ key: 'processDependencies', value: null });
                this.priceService.count();
            }
        });
    }

    setDefaultCylinderToAdditionalLock(conf = this.configurationsService.conf.Current) {
        const cylinders = this.getCylindersToAdditionalLock(conf);
        if (cylinders.length > 0) {
            if (!cylinders.map(p => castToNullableNumber(p.id)).includes(conf.doorHardware.cylinders.additionalLock)) {
                this.setCylinderToAdditionalLock(Number(cylinders[0].id));
            } else {
                const additionalCylinder = this.cylindersToAdditionalLock.find(
                    c => castToNullableNumber(c.id) === conf.doorHardware.cylinders.additionalLock
                );
                if (
                    additionalCylinder?.additional_cylinder_related_to_main === false
                    && conf.doorHardware.cylinders.additionalLockColorIsDefault !== false
                ) {
                    this.setDefaultColorForAdditionalCylinder(conf);
                }
            }
        } else {
            this.setCylinderToAdditionalLock(null);
        }
    }

    openModalCylinderToMainLockOptions( conf = this.configurationsService.conf.Current) {
        const cylinderId = conf.doorHardware && conf.doorHardware.cylinders.mainLock;
        const selectedType = this.cylindersToMainLock.find(f => Number(f.id) === Number(cylinderId));
        const selectedMainLockColor = conf.doorHardware.cylinders.mainLockColor || conf.doorHardware.cylinders.color;
        const matchedColor = this.hardwareColors.find(el => Number(el.id) === Number(selectedMainLockColor));
        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypeOptionsPageComponent,
            resolve: {
                title: () => _('DOOR|Opcje wkładki zamka głównego'),
                selectedType: () => selectedType,
                imageUrl: () => '/files/windowaccessory',
                hardwareColors: () => this.hardwareColors,
                selColor: () => matchedColor,
            },
        });

        modalInstance.result.then((selectedData) => {
            if (isObject(selectedData) && isDefined(selectedData.type)) {
                this.setMainLockColorById(selectedData.color, selectedData.defaultColor);
            }
            this.priceService.count();
            this.timeLimitService.count();
        });

        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg',
                });
            }
        });
    }

    setMainLockColorById(colorId, isDefault = false) {
        const matchedColors = this.hardwareColors.filter(el => Number(el.id) === Number(colorId));

        if (!(matchedColors.length > 0) || !matchedColors[0]) {
            this.setMainLockColor(null);
            return;
        }

        const color = core.copy(matchedColors[0]);
        color.isDefault = isDefault;
        this.setMainLockColor(color);
    }

    setMainLockColor(color: IccAccessoryColor, conf = this.configurationsService.conf.Current) {
        const colorId = (color && Number(color.id)) || null;
        conf.doorHardware.cylinders.mainLockColor = colorId;
        conf.doorHardware.cylinders.color = colorId;
        conf.doorHardware.cylinders.mainLockColorIsDefault = color?.isDefault ?? false;
        this.setColorsBasedOnColorMapping(Number(color?.id), 'cylinder');
        this.emitEvent();
    }

    setAdditionalLockColorById(colorId, isDefault = false) {
        const matchedColors = this.hardwareColors.filter(el => Number(el.id) === Number(colorId));

        if (!(matchedColors.length > 0) || !matchedColors[0]) {
            this.setAdditionalLockColor(null);
            return;
        }

        const color = core.copy(matchedColors[0]);
        color.isDefault = isDefault;
        this.setAdditionalLockColor(color, undefined, isDefault);
    }

    setAdditionalLockColor(
        color: IccAccessoryColor,
        conf = this.configurationsService.conf.Current,
        isDefault = false
    ) {
        conf.doorHardware.cylinders.additionalLockColor = (color && Number(color.id)) || null;
        conf.doorHardware.cylinders.additionalLockColorIsDefault = isDefault;
        this.emitEvent();
    }

    setElectronicLockColorById(colorId, isDefault = false) {
        const matchedColors = this.hardwareColors.filter(el => Number(el.id) === colorId);

        if (!(matchedColors.length > 0) || !matchedColors[0]) {
            this.setElectronicLockColor(null);
            return;
        }

        const color = core.copy(matchedColors[0]);
        color.isDefault = isDefault;
        this.setElectronicLockColor(color);
    }

    setElectronicLockColor(color: IccAccessoryColor, conf = this.configurationsService.conf.Current) {
        if (conf.doorHardware.electronicLock) {
            conf.doorHardware.electronicLock.color = (color && Number(color.id)) || null;
            conf.doorHardware.electronicLock.colorIsDefault = color?.isDefault ?? false;
            this.setColorsBasedOnColorMapping(Number(color?.id), 'electronicLock');
            this.emitEvent();
        }
    }

    openModalCylinderToAdditionalLockOptions(conf = this.configurationsService.conf.Current) {
        const cylinderId = conf.doorHardware && conf.doorHardware.cylinders.additionalLock;
        const selectedType = this.cylindersToAdditionalLock.find(f => Number(f.id) === Number(cylinderId));
        const selectedAdditionalLockColor = this.hardwareColors.find(
            el => Number(el.id) === Number(conf.doorHardware.cylinders.additionalLockColor)
        );
        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypeOptionsPageComponent,
            resolve: {
                title: () => _('DOOR|Opcji wkładki zamka dodatkowego'),
                selectedType: () => selectedType,
                imageUrl: () => '/files/windowaccessory',
                hardwareColors: () => this.hardwareColors,
                selColor: () => selectedAdditionalLockColor,
            },
        });

          modalInstance.result.then((selectedData) => {
            if (isObject(selectedData) && isDefined(selectedData.type)) {
                this.setAdditionalLockColorById(selectedData.color, selectedData.defaultColor);
            }
            this.priceService.count();
            this.timeLimitService.count();
        });

        modalInstance.closed.then(() => {
            if (this.config().IccConfig.Configurators.tutorialAvailable) {
                this.eventBusService.post({
                    key: 'tutorialSteps',
                    value: 'getStepImg',
                });
            }
        });
    }

    setCylinderToAdditionalLock(id: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.cylinders.additionalLock = id;
        if (id) {
            this.setDefaultColorForAdditionalCylinder(conf);
        } else {
            this.setCylinderAdditionalLockColor(null, conf);
        }
        this.setKeyOptionsForAdditionalLock(conf);
        this.emitEvent();
    }

    getCylinderSystems(conf = this.configurationsService.conf.Current) {
        const cylinderToMainLock = this.cylindersToMainLock.find(
            p => castToNullableNumber(p.id) === conf.doorHardware.cylinders.mainLock
        );
        return this.cylinderSystems.filter(
            system =>
                cylinderToMainLock
                && Number(cylinderToMainLock.window_accessory_system_id) === Number(system.id)
        );
    }

    openCylinderSystemModal(conf = this.configurationsService.conf.Current) {
        const modalInstance = this.modalService.open({
            pageComponent: ListPageComponent,
            resolve: {
                title: () => _('DOOR|Systemy wkładek'),
                selectedId: () => conf.doorHardware.cylinders.system,
                items: () => this.getCylinderSystems(conf),
                imageUrl: () => '/files/windowaccessorysystem',
            },
        });

        modalInstance.result.then(selected => {
            if (isObject(selected) && isDefined(selected)) {
                this.setCylinderSystem(Number(selected.id));
            }
        });
    }

    setDefaultCylinderSystem(conf = this.configurationsService.conf.Current) {
        const system = this.getCylinderSystems(conf);
        if (system.length > 0) {
            if (!system.map(p => castToNullableNumber(p.id)).includes(conf.doorHardware.cylinders.system)) {
                this.setCylinderSystem(Number(system[0].id));
            }
        } else {
            this.setCylinderSystem(null);
        }
    }

    setCylinderSystem(id: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.cylinders.system = id;
        this.emitEvent();
    }

    changeDoorViewerPunching(value: boolean, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.doorViewerPunching = value;
        this.emitEvent();
        this.eventBusService.post({ key: 'processDependencies', value: null });
    }

    platesBoxSelect = (conf: DoorActiveConfiguration) => {
        const mainLockPlate = (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToMainLock : this.platesToMainLock).find(
            t => Number(t.id) === conf.doorHardware.plates.mainLock
        );
        const additionalLockPlate = (conf.doorHardware.set === 'mixedLever' ? this.innerPlatesToAdditionalLock : this.platesToAdditionalLock).find(
            t => Number(t.id) === conf.doorHardware.plates.additionalLock
        );
        const matchedColor = this.hardwareColors.find(
            (el) => Number(el.id) ===  conf.doorHardware.plates.color
        );
        const img = mainLockPlate ? '/files/windowaccessory/' + mainLockPlate.img : '';

        const mainLockName = mainLockPlate ? mainLockPlate.name : '';
        const additionalLockName = additionalLockPlate ? additionalLockPlate.name : '';
        const color = matchedColor ? matchedColor.name : null;
        const colorId = matchedColor ? matchedColor.id : null;
        return {
            img,
            mainLockName,
            additionalLockName,
            color,
            colorId,
        };
    };

    outerPlatesBoxSelect = (conf: DoorActiveConfiguration) => {
        const mainLockPlate = this.outerPlatesToMainLock.find(
            t => Number(t.id) === Number(conf.doorHardware.outerPlates.mainLock)
        );
        const additionalLockPlate = this.outerPlatesToAdditionalLock .find(
            t => Number(t.id) === conf.doorHardware.outerPlates.additionalLock
        );
        const matchedColor = this.hardwareColors.find(
            (el) => Number(el.id) ===  conf.doorHardware.outerPlates.color
        );
        const img = mainLockPlate ? '/files/windowaccessory/' + mainLockPlate.img : '';

        const mainLockName = mainLockPlate ? mainLockPlate.name : '';
        const additionalLockName = additionalLockPlate ? additionalLockPlate.name : '';
        const color = matchedColor ? matchedColor.name : null;
        const colorId = matchedColor ? matchedColor.id : null;
        return {
            img,
            mainLockName,
            additionalLockName,
            color,
            colorId,
        };
    };

    pullsBoxSelect = (conf: DoorActiveConfiguration) => {
        const pullType = this.pulls.find(t => Number(t.id) === conf.doorHardware.pull);
        const matchedColor = this.hardwareColors.find(el => Number(el.id) === conf.doorHardware.pullColor);
        const name = pullType?.name;
        const color = matchedColor ? matchedColor.name : null;
        const colorId = matchedColor ? matchedColor.id : null;
        const imagePath = this.getImageUrl(pullType, colorId);

        return {
           pullType: {
               name,
               color,
               colorId,
               imagePath
           }
        }
    }

    recessedPullsBoxSelect = (conf: DoorActiveConfiguration) => {
        const pullType = this.recessedPulls.find(t => Number(t.id) === conf.doorHardware.recessedPull);

        const name = pullType?.name;
        const color = conf.doorHardware.recessedPullColor ? conf.doorHardware.recessedPullColor.color.name : null;
        const colorId = String(conf.doorHardware.recessedPullColor ? conf.doorHardware.recessedPullColor.color.id : '');
        const imagePath = '/files/windowaccessory/' + pullType?.img;

        return {
           pullType: {
               name,
               color,
               colorId,
               imagePath
           }
        }
    }

    locksBoxSelect = (conf: DoorActiveConfiguration) => {
        const lock = this.locksService.locks.find(l => Number(l.id) === Number(conf.Lock.id));
        const name = lock?.name;
        return {
            lock: {
                name,
            }
        }
    }

    electronicLockBoxSelect = (conf: DoorActiveConfiguration) => {
        const electronicLock = this.electronicLocks.find(
            t => Number(t.id) === conf.doorHardware.electronicLock?.id
        )

        const electronicLockName = electronicLock?.name;

        if (electronicLock?.colors_ids === null) {
            return {
                electronicLockName
            };
        }

        const color =
        this.hardwareColors.find(
            (el) => Number(el.id) === Number(conf.doorHardware.electronicLock?.color)
        )
        const electronicLockColor = color?.name;

        const imagePath = this.getFirstImagePathFromCylinders(color?.id ?? '', electronicLock);

        return {
            electronicLockName,
            electronicLockColor,
            imagePath
        }
    }

    doorViewerBoxSelect = (conf: DoorActiveConfiguration) => {
        const doorViewer = this.doorViewers.find(doorViewer => Number(doorViewer.id) === conf.doorHardware.doorViewer)

        const doorViewerName = doorViewer?.name;
        if (doorViewer?.colors_ids === null || !doorViewer?.colors_ids) {
            return {
                name: doorViewerName,
                color: null,
                imagePath: null
            };
        }

        const color = this.hardwareColors.find(color => Number(color.id) === Number(conf.doorHardware.doorViewerColor));
        const doorViewerColor = color?.name;

        const imagePath = this.getFirstImagePathFromCylinders(color?.id ?? '', doorViewer);

        return {
            name: doorViewerName,
            color: doorViewerColor,
            imagePath
        }
    }

    cylindersBoxSelect = (conf: DoorActiveConfiguration) => {
        const mainLockCylinder = this.cylindersToMainLock.find(
            t => Number(t.id) === conf.doorHardware.cylinders.mainLock
        );

        const selectedMainLockColor = conf.doorHardware.cylinders.mainLockColor || conf.doorHardware.cylinders.color;

        const mainLockColor =
            this.hardwareColors.find(
                (el) => Number(el.id) === Number(selectedMainLockColor)
            );

        const additionalLockColor = this.hardwareColors.find(
            (el) => Number(el.id) === Number(conf.doorHardware.cylinders.additionalLockColor)
        );

        const additionalLockCylinder = this.cylindersToAdditionalLock.find(
            t => Number(t.id) === conf.doorHardware.cylinders.additionalLock
        );
        const cylinderSystem = this.cylinderSystems.find(
            t => Number(t.id) === conf.doorHardware.cylinders.system
        );
        const electronicLockObject = this.electronicLocks.find(
            t => Number(t.id) === conf.doorHardware.electronicLock?.id
        );

        const imagePath = this.getFirstImagePathFromCylinders(
            mainLockColor?.id ?? '',
            mainLockCylinder,
            additionalLockCylinder,
            cylinderSystem,
            electronicLockObject,
        );

        const mainLockName = mainLockCylinder?.name ?? '';
        const additionalLockName = additionalLockCylinder?.name ?? '';
        const system = cylinderSystem?.name ?? '';
        const electronicLock =electronicLockObject?.name ?? '';
        return {
            cylinders: {
                imagePath,
                mainLockName,
                additionalLockName,
                color: mainLockColor?.name ?? '',
                mainLockColor: mainLockColor?.name ?? '',
                additionalLockColor: additionalLockColor?.name ?? '',
                system,
                electronicLock,
            },
        };
    };

    getFirstImagePathFromCylinders(colorId, ...cylindersBox) {
        const item = cylindersBox.find(i => i !== undefined);
        if(item && colorId) {
            return this.getImageUrl(item, colorId)
        } else if (item?.img) {
            return '/files/windowaccessory/' + item?.img;
        } else {
            return null;
        }
    }

    setDefaultDecorativeHingeCover(conf = this.configurationsService.conf.Current) {
        const covers = this.decoHingeCovers;
        if (covers.length > 0 && conf.System && conf.System.decorative_hinge_covers) {
            if (!covers.map(p => castToNullableNumber(p.id)).includes(conf.doorHardware.decorativeHingeCovers)) {
                this.setDecorativeHingeCover(Number(covers[0].id));
            }
        } else {
            this.setDecorativeHingeCover(null);
        }
    }

    setDecorativeHingeCover(id: number | null, conf = this.configurationsService.conf.Current) {
        conf.doorHardware.decorativeHingeCovers = id;
        this.setDefaultColorForDecorativeHingeCover(conf);
        this.emitEvent();
    }

    openModalDecorativeHingeCoverOptions() {
        const matchedColor = this.hardwareColors.find(
            (el) =>
                Number(el.id) ===
                this.configurationsService.conf.Current.doorHardware.decorativeHingeCoversColor
        );
        const hingeCoverId =
            this.configurationsService.conf.Current.doorHardware
            && this.configurationsService.conf.Current.doorHardware.decorativeHingeCovers;
        const cover = this.decoHingeCovers.find(f => Number(f.id) === Number(hingeCoverId));
        const modalInstance = this.modalService.open({
            pageComponent: HardwareTypeOptionsPageComponent,
            resolve: {
                title: () => _('WINDOW|Ozdobne nakładki zawiasów'),
                selectedType: () => cover,
                hardwareColors: () => this.hardwareColors,
                selColor: () => matchedColor,
            },
        });

        modalInstance.result.then(selectedData => {
            if (isObject(selectedData) && isDefined(selectedData.type)) {
                this.setDecorativeHingeCoverColorById(selectedData.color, selectedData.defaultColor);
            }
        });
    }

    /**
     * Funkcja ustawiajca kolor ozdobnych nakładek zawiasów
     * @param {object} color Kolor
     */
    setDecorativeHingeCoverColor(
        color: IccAccessoryColor | null,
        conf = this.configurationsService.conf.Current
    ) {
        conf.doorHardware.decorativeHingeCoversColor = color ? Number(color.id) : null;
        conf.doorHardware.decorativeHingeCoversColorIsDefault = color ? color.isDefault : false;
        this.emitEvent();
    }

    /**
     * Funkcja ustawiająca kolor dla ozdobnych nakładek zawiasów
     * @param  {number} colorId  Id koloru
     */
    setDecorativeHingeCoverColorById(colorId, isDefault = false) {
        const matchedColors = this.hardwareColors.filter(el => el.id == colorId);
        if (matchedColors.length > 0) {
            const color = core.copy(matchedColors[0]);
            color.isDefault = isDefault;
            this.setDecorativeHingeCoverColor(color);
        } else {
            this.setDecorativeHingeCoverColor(null);
        }
    }

    /**
     * Funkcja ustawiająca domyslny kolor dla ozdobnych nakładek zawiasów
     */
    setDefaultColorForDecorativeHingeCover(conf = this.configurationsService.conf.Current) {
        const cover = this.decoHingeCovers.find(
            t => Number(t.id) === conf.doorHardware.decorativeHingeCovers
        );

        if(cover?.color_related_to_plate && conf.doorHardware.plates?.color) {
            this.setDecorativeHingeCoverColorById(conf.doorHardware.plates.color, true);
        } else if (isObject(cover) && isArray(cover.colors_ids) && isArray(this.hardwareColors)) {
            const categoryColor = this.findColorFromCategoryMapping(cover, conf);
            if (categoryColor) {
                this.setDecorativeHingeCoverColor(categoryColor, conf);
                return;
            }
            const windowColorId = this.colorMappingService.getWindowColorId(conf, 'inner');
            const matchedColors = this.colorMappingService.getColors(
                Number(windowColorId),
                'window',
                'accessory'
            );

            const matchedColors2 = this.colorMappingService.getColors(
                Number(windowColorId),
                'accessory',
                'accessory'
            );
            const colors = this.hardwareColors.filter(el => cover.colors_ids.indexOf(el.id) > -1);
            if (isArray(colors) && isDefined(colors[0])) {
                const windowColors = matchedColors
                    .map(m => colors.filter(c => Number(c.id) === m)[0])
                    .filter(m => m);
                let color;
                if (isArray(windowColors) && isDefined(windowColors[0])) {
                    color = core.copy(windowColors[0]);
                } else {
                    color = core.copy(colors[0]);
                }
                color.isDefault = true;
                this.setDecorativeHingeCoverColor(color, conf);
            }
        } else {
            this.setDecorativeHingeCoverColor(null, conf);
        }
    }

    /**
     * Each accessory has an assigned category.
     * Returns @iccListTab array based on the provided @accessories array.
     * @returns {iccListTab}
     */
    private getTabsBasedOnWindowAccessoriesCategoryId(accessories) {
        const accessoriesProjection: iccListTab[] = [];

        const uniqueCategoriesIds = Array.from(
            new Set(accessories.map((p) => p.window_accessories_category_id))
        );

        const categoriesName = this.configuratorsDataService.data.windowAccessoriesCategories.filter(
            (c) => uniqueCategoriesIds.some((x) => Number(x) === Number(c.id))
        );

        categoriesName.forEach(a => {
            accessoriesProjection.push({id: Number(a.id), name: a.name} );
        });

        return accessoriesProjection;
    }

    private emitEvent() {
        this.updateDictionary();
        this.eventBusService.post({
            key: 'changedDoorHardware',
            value: null,
        });
    }

    setColorsBasedOnColorMapping(
        colorId: number,
        passedType?: 'pull' | 'decoHingeCovers' | 'handle' | 'cylinder' | 'accessory' | 'plates' | 'electronicLock' | 'outerPlates' | 'doorViewer',
        conf = this.configurationsService.conf.Current
    ) {
        if (!colorId) return;
        this.colorIdToMapping = colorId;
        const matchedColors = this.colorMappingService.getMappedColorsFromAccessory(colorId);
        let matchedColor = [];

        if (passedType !== 'cylinder' && conf.doorHardware) {
            const cylinderItem = this.cylindersToMainLock.find(
                (p) => Number(p.id) === Number(conf.doorHardware.cylinders.mainLock)
            );
            if (!this.itemHasCategoryMapping(cylinderItem)) {
                matchedColor = matchedColors?.filter((el) =>
                    cylinderItem?.colors_ids?.some((p) => Number(p) === Number(el))
                );
                if (matchedColor && matchedColor[0]) {
                    conf.doorHardware.cylinders.mainLockColor = matchedColor[0];
                    conf.doorHardware.cylinders.color = matchedColor[0];
                }
            }
        }

        if (conf.doorHardware
            && conf.doorHardware.cylinders.additionalLock != null
            && conf.doorHardware.cylinders.additionalLockColorIsDefault !== false) {
            const additionalCylinder = this.cylindersToAdditionalLock.find(
                (p) => Number(p.id) === Number(conf.doorHardware.cylinders.additionalLock)
            );
            const additionalCylinderColors = additionalCylinder?.colors_ids;

            if (additionalCylinder?.additional_cylinder_related_to_main !== false) {
                const mainLockColorId = conf.doorHardware.cylinders.mainLockColor
                    ?? conf.doorHardware.cylinders.color;
                if (mainLockColorId != null) {
                    const isDirectMatch = additionalCylinderColors?.some(
                        (el) => Number(el) === Number(mainLockColorId)
                    );
                    if (isDirectMatch) {
                        conf.doorHardware.cylinders.additionalLockColor = mainLockColorId;
                    } else {
                        const mappedFromMain = this.colorMappingService.getMappedColorsFromAccessory(Number(mainLockColorId));
                        const matched = mappedFromMain?.filter((el: number) =>
                            additionalCylinderColors?.some((p: number) => Number(p) === Number(el))
                        );
                        if (matched?.[0]) {
                            conf.doorHardware.cylinders.additionalLockColor = matched[0];
                        }
                    }
                }
            } else {
                matchedColor = matchedColors?.filter((el) =>
                    additionalCylinderColors?.some((p) => Number(p) === Number(el))
                );
                if (matchedColor && matchedColor[0]) {
                    conf.doorHardware.cylinders.additionalLockColor = matchedColor[0];
                }
            }
        }

        if (passedType !== 'electronicLock' && conf.doorHardware) {
            const electronicLockItem = this.electronicLocks.find(
                (p) => Number(p.id) === Number(conf.doorHardware.electronicLock?.id)
            );
            if (!this.itemHasCategoryMapping(electronicLockItem)) {
                matchedColor = matchedColors?.filter((el) =>
                    electronicLockItem?.colors_ids?.some((p) => Number(p) === Number(el))
                );
                if (matchedColor && matchedColor[0]) {
                    conf.doorHardware.electronicLock.color = matchedColor[0];
                }
            }
        }

        if (passedType !== 'doorViewer'&& conf.doorHardware && conf.doorHardware.doorViewerColorIsDefault) {
            const doorViewer = this.doorViewers.find(
                (p) => Number(p.id) === Number(conf.doorHardware.doorViewer)
            )?.colors_ids;
            matchedColor = matchedColors?.filter((el) =>
                doorViewer?.some((p) => Number(p) === Number(el))
            );
            if (matchedColor && matchedColor[0]) {
                conf.doorHardware.doorViewerColor = matchedColor[0];
            } else if(doorViewer?.length > 0 && !doorViewer.map(Number).includes(Number(conf.doorHardware.doorViewerColor))) {
                conf.doorHardware.doorViewerColor = Number(doorViewer[0]);
            }
        }

        if (passedType !== 'decoHingeCovers' && passedType === 'plates' && conf.doorHardware && (conf.doorHardware.decorativeHingeCoversColorIsDefault || conf.doorHardware.decorativeHingeCoversColorRelatedToPlate)) {
            const decoHingeCovers = this.decoHingeCovers.find(
                (p) => Number(p.id) === Number(conf.doorHardware.decorativeHingeCovers)
            )?.colors_ids;
            matchedColor = matchedColors?.filter((el) =>
                decoHingeCovers?.some((p) => Number(p) === Number(el))
            );
            if (matchedColor && matchedColor[0]) {
                conf.doorHardware.decorativeHingeCoversColor = matchedColor[0];
            } else if(decoHingeCovers?.length > 0 && !decoHingeCovers.map(Number).includes(Number(conf.doorHardware.decorativeHingeCoversColor))) {
                conf.doorHardware.decorativeHingeCoversColor = Number(decoHingeCovers[0]);
            }
        }

        const handleColorsAll = this.configuratorsDataService.data.windowHandlesColors;
        const hingeColors = conf.Hinge?.colors_ids;
        matchedColor = matchedColors?.filter((el) =>
            hingeColors?.some((p) => Number(p) === Number(el))
        );
        if (matchedColor && matchedColor[0]) {
            const color = handleColorsAll.find((p) => Number(p.id) === Number(matchedColor[0]));
            conf.HingeColor = color;
        }

        if (passedType !== 'plates' && conf.doorHardware) {
            const plateItem = this.platesToMainLock.find(
                (p) => Number(p.id) === Number(conf.doorHardware.plates.mainLock)
            );
            if (!this.itemHasCategoryMapping(plateItem)) {
                matchedColor = matchedColors?.filter((el) =>
                    plateItem?.colors_ids?.some((p) => Number(p) === Number(el))
                );
                if (matchedColor && matchedColor[0]) {
                    conf.doorHardware.plates.color = matchedColor[0];
                }
            }
            if((conf.doorHardware.decorativeHingeCoversColorIsDefault || conf.doorHardware.decorativeHingeCoversColorRelatedToPlate) && conf.doorHardware.plates.color) {
                const matchedHingeColors = this.colorMappingService.getMappedColorsFromAccessory(conf.doorHardware.plates.color);

                const decoHingeCovers = this.decoHingeCovers.find(
                    (p) => Number(p.id) === Number(conf.doorHardware.decorativeHingeCovers)
                )?.colors_ids;
                matchedColor = matchedHingeColors?.filter((el) =>
                    decoHingeCovers?.some((p) => Number(p) === Number(el))
                );
                if (matchedColor && matchedColor[0]) {
                    conf.doorHardware.decorativeHingeCoversColor = matchedColor[0];
                } else if(decoHingeCovers?.length > 0 && !decoHingeCovers.map(Number).includes(Number(conf.doorHardware.decorativeHingeCoversColor))) {
                    conf.doorHardware.decorativeHingeCoversColor = Number(decoHingeCovers[0]);
                }
            }
        }

        if (passedType !== 'outerPlates' && conf.doorHardware) {
            const outerPlateItem = this.outerPlatesToMainLock.find(
                (p) => Number(p.id) === Number(conf.doorHardware.outerPlates.mainLock)
            );
            if (!this.itemHasCategoryMapping(outerPlateItem)) {
                matchedColor = matchedColors?.filter((el) =>
                    outerPlateItem?.colors_ids?.some((p) => Number(p) === Number(el))
                );
                if (matchedColor && matchedColor[0]) {
                    conf.doorHardware.outerPlates.color = matchedColor[0];
                }
            }
        }

        if (passedType !== 'pull' && conf.doorHardware) {
            const pullItem = this.pulls.find((p) => Number(p.id) === Number(conf.doorHardware.pull));
            if (!this.itemHasCategoryMapping(pullItem)) {
                matchedColor = matchedColors?.filter((el) =>
                    pullItem?.colors_ids?.some((p) => Number(p) === Number(el))
                );
                if (matchedColor && matchedColor[0]) {
                    conf.doorHardware.pullColor = matchedColor[0];
                }
            }
        }

        if (passedType !== 'accessory') {
            const accessories = conf.Accessories;
            accessories.forEach(a => {
                if (a.accessory.changedColor || this.itemHasCategoryMapping(a.accessory)) {
                    return;
                }
                matchedColor = matchedColors?.filter((el) => a.accessory.colors_ids?.some(p => Number(p) === Number(el)));

                if (matchedColor && matchedColor[0]) {
                    a.color = matchedColor[0];
                    a.accessory.color = this.accessoriesService.colorsAll.find(p => Number(p.id) === Number(matchedColor[0]));
                    a.accessory.selectedColor = matchedColor[0];
                }

            })
        }
    }


    private updateDictionary(conf = this.configurationsService.conf.Current) {
        if (this.configuratorsDataService) {
            if(conf.doorHardware.doorHandleAdjustment) {
                const doorHandleAdjustment = this.configuratorsDataService.get(
                    'doorHandleAdjustments',
                    conf.doorHardware.doorHandleAdjustment
                );
                if (doorHandleAdjustment) {
                    conf.doorHardwareDictionary.doorHardware[doorHandleAdjustment.id] = {
                        id: doorHandleAdjustment.id,
                        name: doorHandleAdjustment.name,
                    };
                }
            }

            if(conf.doorHardware.pull) {
                const pull = this.configuratorsDataService.get('pullType', conf.doorHardware.pull);
                if (pull) {
                    conf.doorHardwareDictionary.doorHardware[pull.id] = {
                        id: pull.id,
                        name: pull.name,
                    };
                    const color = conf.doorHardware.pullColor || conf.doorHardware.color;
                    conf.doorHardware.visualizations.pull =
                        pull.visualizations
                        && ((color && pull.visualizations[color]) || pull.visualizations[-1]);
                }
            }

            if( conf.doorHardware.pullColor) {
                const pullColor = this.configuratorsDataService.get(
                    'windowHandlesColors',
                    conf.doorHardware.pullColor
                );
                if (pullColor) {
                    conf.doorHardwareDictionary.accessoryColors[Number(pullColor.id)] = {
                        id: Number(pullColor.id),
                        name: pullColor.name,
                        color: pullColor.color,
                        code: pullColor.code,
                        colorImg: pullColor.color_img,
                    };
                }
            }

            if(conf.doorHardware.electronicLock?.id) {
                const electronicLock = this.configuratorsDataService.get(
                    'electronicLocks',
                    conf.doorHardware.electronicLock?.id
                ); // performance now;
                // performace mark nazwa punktu w czasie
                // perforemace mesure dostaje dwa perrfoermace mark
                // performace wyśweitla czas mesure
                // performace do podsuwamonia
                if (electronicLock) {
                    conf.doorHardwareDictionary.doorHardware[electronicLock.id] = {
                        id: electronicLock.id,
                        name: electronicLock.name,
                    };
                    if(conf.doorHardware.electronicLock.color) {
                        const electronicLockColor = this.configuratorsDataService.get(
                            'windowHandlesColors',
                            conf.doorHardware.electronicLock.color
                        );
                        if (electronicLockColor) {
                            conf.doorHardwareDictionary.accessoryColors[Number(electronicLockColor.id)] = {
                                id: Number(electronicLockColor.id),
                                name: electronicLockColor.name,
                                color: electronicLockColor.color,
                                code: electronicLockColor.code,
                                colorImg: electronicLockColor.color_img,
                            }
                        }
                    }
                }
            }

            if(conf.doorHardware.plateType) {
                const plateType = this.configuratorsDataService.get(
                    'plateTypes',
                    conf.doorHardware.plateType
                );
                if (plateType) {
                    conf.doorHardwareDictionary.doorHardware[plateType.id] = {
                        id: plateType.id,
                        name: plateType.name,
                        backset: Number(plateType.backset),
                        distanceToAdditionalLock: Number(plateType.distance_to_additional_lock),
                        lockSpacing: Number(plateType.lock_spacing),
                    };
                }
            }

            if(conf.doorHardware.doorViewer) {
                const doorViewer = this.configuratorsDataService.get(
                    'doorViewers',
                    conf.doorHardware.doorViewer
                );
                if (doorViewer) {
                    conf.doorHardwareDictionary.doorHardware[doorViewer.id] = {
                        id: doorViewer.id,
                        name: doorViewer.name,
                    };
                    const color = conf.doorHardware.doorViewerColor || conf.doorHardware.plates?.color || conf.doorHardware.color;
                    conf.doorHardware.visualizations.doorViewer =
                        doorViewer.visualizations
                        && ((color && doorViewer.visualizations[color]) || doorViewer.visualizations[-1]);
                }
            }

            if(conf.doorHardware.doorViewerColor) {
                const doorViewerColor = this.configuratorsDataService.get(
                    'windowHandlesColors',
                    conf.doorHardware.doorViewerColor
                );
                if (doorViewerColor) {
                    conf.doorHardwareDictionary.accessoryColors[Number(doorViewerColor.id)] = {
                        id: Number(doorViewerColor.id),
                        name: doorViewerColor.name,
                        color: doorViewerColor.color,
                        code: doorViewerColor.code,
                        colorImg: doorViewerColor.color_img,
                    };
                }
            }

            if(conf.doorHardware.plates.additionalLock) {
                const additionalLockPlate = this.configuratorsDataService.get(
                    'plates',
                    conf.doorHardware.plates.additionalLock
                );
                if (additionalLockPlate) {
                    conf.doorHardwareDictionary.doorHardware[additionalLockPlate.id] = {
                        id: additionalLockPlate.id,
                        name: additionalLockPlate.name,
                        backset: Number(additionalLockPlate.backset),
                        distanceToAdditionalLock: Number(
                            additionalLockPlate.distance_to_additional_lock
                        ),
                        lockSpacing: Number(additionalLockPlate.lock_spacing),
                    };
                    const color = conf.doorHardware.plates.color || conf.doorHardware.color;
                    conf.doorHardware.visualizations.additionalLockPlate =
                        additionalLockPlate.visualizations
                        && ((color && additionalLockPlate.visualizations[color])
                            || additionalLockPlate.visualizations[-1]);
                }
            }

            if(conf.doorHardware.outerPlates.additionalLock) {
                const outerAdditionalLockPlate = this.configuratorsDataService.get(
                    'plates',
                    conf.doorHardware.outerPlates.additionalLock
                );
                if (outerAdditionalLockPlate) {
                    conf.doorHardwareDictionary.doorHardware[outerAdditionalLockPlate.id] = {
                        id: outerAdditionalLockPlate.id,
                        name: outerAdditionalLockPlate.name,
                        backset: Number(outerAdditionalLockPlate.backset),
                        distanceToAdditionalLock: Number(
                            outerAdditionalLockPlate.distance_to_additional_lock
                        ),
                        lockSpacing: Number(outerAdditionalLockPlate.lock_spacing),
                    };
                    const color = conf.doorHardware.outerPlates.color || conf.doorHardware.color;
                    conf.doorHardware.visualizations.outerAdditionalLockPlate =
                        outerAdditionalLockPlate.visualizations
                        && ((color && outerAdditionalLockPlate.visualizations[color])
                            || outerAdditionalLockPlate.visualizations[-1]);
                }
            }

            if(conf.doorHardware.cylinders.mainLock) {
                const mainLockCylinder = this.configuratorsDataService.get(
                    'cylinders',
                    conf.doorHardware.cylinders.mainLock
                );
                if (mainLockCylinder) {
                    conf.doorHardwareDictionary.doorHardware[mainLockCylinder.id] = {
                        id: mainLockCylinder.id,
                        name: mainLockCylinder.name,
                        backset: Number(mainLockCylinder.backset),
                        distanceToAdditionalLock: Number(mainLockCylinder.distance_to_additional_lock),
                        lockSpacing: Number(mainLockCylinder.lock_spacing),
                    };
                    const color = conf.doorHardware.cylinders.mainLockColor
                        || conf.doorHardware.cylinders.color
                        || conf.doorHardware.plates.color
                        || conf.doorHardware.color;
                    conf.doorHardware.visualizations.mainLockCylinder =
                        mainLockCylinder.visualizations
                        && ((color && mainLockCylinder.visualizations[color])
                            || mainLockCylinder.visualizations[-1]);
                }
            }

            if(conf.doorHardware.cylinders.additionalLock) {
                const additionalLockCylinder = this.configuratorsDataService.get(
                    'cylinders',
                    conf.doorHardware.cylinders.additionalLock
                );
                if (additionalLockCylinder) {
                    conf.doorHardwareDictionary.doorHardware[additionalLockCylinder.id] = {
                        id: additionalLockCylinder.id,
                        name: additionalLockCylinder.name,
                        backset: Number(additionalLockCylinder.backset),
                        distanceToAdditionalLock: Number(
                            additionalLockCylinder.distance_to_additional_lock
                        ),
                        lockSpacing: Number(additionalLockCylinder.lock_spacing),
                    };
                    const color = conf.doorHardware.cylinders.additionalLockColor;
                    conf.doorHardware.visualizations.additionalLockCylinder =
                        additionalLockCylinder.visualizations
                        && ((color && additionalLockCylinder.visualizations[color])
                            || additionalLockCylinder.visualizations[-1]);
                }
            }

            if(conf.doorHardware.type) {
                const type = this.configuratorsDataService.get('hardwareTypes', conf.doorHardware.type);
                if (type) {
                    conf.doorHardwareDictionary.doorHardware[type.id] = {
                        id: type.id,
                        name: type.name,
                        backset: Number(type.backset),
                        distanceToAdditionalLock: Number(type.distance_to_additional_lock),
                        lockSpacing: Number(type.lock_spacing),
                    };
                }
            }
            if( conf.doorHardware.cylinders.system) {
                const system = this.configuratorsDataService.get(
                    'hardwareSystems',
                    conf.doorHardware.cylinders.system
                );
                if (system) {
                    conf.doorHardwareDictionary.hardwareSystems[system.id] = {
                        id: system.id,
                        name: system.name,
                    };
                }
            }
            if(conf.doorHardware.plates.mainLock) {
                const mainLockPlate = this.configuratorsDataService.get(
                    'plates',
                    conf.doorHardware.plates.mainLock
                );
                if (mainLockPlate) {
                    conf.doorHardwareDictionary.doorHardware[mainLockPlate.id] = {
                        id: mainLockPlate.id,
                        name: mainLockPlate.name,
                        backset: Number(mainLockPlate.backset),
                        distanceToAdditionalLock: Number(mainLockPlate.distance_to_additional_lock),
                        lockSpacing: Number(mainLockPlate.lock_spacing),
                    };
                    const color = conf.doorHardware.plates.color || conf.doorHardware.color;
                    conf.doorHardware.visualizations.mainLockPlate =
                        mainLockPlate.visualizations
                        && ((color && mainLockPlate.visualizations[color])
                            || mainLockPlate.visualizations[-1]);
                }
            }
            if(conf.doorHardware.outerPlates.mainLock) {
                const outerMainLockPlate = this.configuratorsDataService.get(
                    'plates',
                    conf.doorHardware.outerPlates.mainLock
                );
                if (outerMainLockPlate) {
                    conf.doorHardwareDictionary.doorHardware[outerMainLockPlate.id] = {
                        id: outerMainLockPlate.id,
                        name: outerMainLockPlate.name,
                        backset: Number(outerMainLockPlate.backset),
                        distanceToAdditionalLock: Number(outerMainLockPlate.distance_to_additional_lock),
                        lockSpacing: Number(outerMainLockPlate.lock_spacing),
                    };
                    const color = conf.doorHardware.outerPlates.color || conf.doorHardware.color;
                    conf.doorHardware.visualizations.outerMainLockPlate =
                        outerMainLockPlate.visualizations
                        && ((color && outerMainLockPlate.visualizations[color])
                            || outerMainLockPlate.visualizations[-1]);
                }
            }

            [
                conf.doorHardware.cylinders.mainLockColor || conf.doorHardware.cylinders.color,
                conf.doorHardware.cylinders.additionalLockColor,
            ].filter(Boolean).forEach((cylinderColorId) => {
                const cylinderColor = this.configuratorsDataService.get(
                    'windowHandlesColors',
                    cylinderColorId
                );
                if (cylinderColor) {
                    conf.doorHardwareDictionary.accessoryColors[Number(cylinderColor.id)] = {
                        id: Number(cylinderColor.id),
                        name: cylinderColor.name,
                        color: cylinderColor.color,
                        code: cylinderColor.code,
                        colorImg: cylinderColor.color_img,
                    };
                }
            });

            if(conf.doorHardware.decorativeHingeCovers) {
                const decorativeHingeCovers = this.configuratorsDataService.get(
                    'decorativeHingeCovers',
                    conf.doorHardware.decorativeHingeCovers
                );

                if(decorativeHingeCovers) {
                    conf.doorHardwareDictionary.doorHardware[decorativeHingeCovers.id] = {
                        id: decorativeHingeCovers.id,
                        name: decorativeHingeCovers.name,
                    };
                    const color = conf.doorHardware.decorativeHingeCoversColor || conf.doorHardware.color;
                    const isAvailableInSystem = decorativeHingeCovers.window_lines_ids.includes(conf.System.id);
                    conf.doorHardware.visualizations.decorativeHingeCovers = isAvailableInSystem && decorativeHingeCovers.visualizations
                        && ((color && decorativeHingeCovers.visualizations[color]) || decorativeHingeCovers.visualizations[-1]);
                    conf.doorHardware.decorativeHingeCoversColorRelatedToPlate = decorativeHingeCovers.color_related_to_plate;
                }
            }

            if(conf.doorHardware.color) {
                const typeColor = this.configuratorsDataService.get(
                    'windowHandlesColors',
                    conf.doorHardware.color
                );
                if (typeColor) {
                    conf.doorHardwareDictionary.accessoryColors[Number(typeColor.id)] = {
                        id: Number(typeColor.id),
                        name: typeColor.name,
                        color: typeColor.color,
                        code: typeColor.code,
                        colorImg: typeColor.color_img,
                    };
                }
            }

            if(conf.doorHardware.plates.color) {
                const plateColor = this.configuratorsDataService.get(
                    'windowHandlesColors',
                    conf.doorHardware.plates.color
                );
                if (plateColor) {
                    conf.doorHardwareDictionary.accessoryColors[Number(plateColor.id)] = {
                        id: Number(plateColor.id),
                        name: plateColor.name,
                        color: plateColor.color,
                        code: plateColor.code,
                        colorImg: plateColor.color_img,
                    };
                }
            }

            if(conf.doorHardware.outerPlates.color) {
                const outerPlateColor = this.configuratorsDataService.get(
                    'windowHandlesColors',
                    conf.doorHardware.outerPlates.color
                );
                if (outerPlateColor) {
                    conf.doorHardwareDictionary.accessoryColors[Number(outerPlateColor.id)] = {
                        id: Number(outerPlateColor.id),
                        name: outerPlateColor.name,
                        color: outerPlateColor.color,
                        code: outerPlateColor.code,
                        colorImg: outerPlateColor.color_img,
                    };
                }
            }

            if(conf.doorHardware.decorativeHingeCoversColor) {
                const decorativeHingeCoversColor = this.configuratorsDataService.get(
                    'windowHandlesColors',
                    conf.doorHardware.decorativeHingeCoversColor
                );
                if (decorativeHingeCoversColor) {
                    conf.doorHardwareDictionary.accessoryColors[
                        Number(decorativeHingeCoversColor.id)
                    ] = {
                        id: Number(decorativeHingeCoversColor.id),
                        name: decorativeHingeCoversColor.name,
                        color: decorativeHingeCoversColor.color,
                        code: decorativeHingeCoversColor.code,
                        colorImg: decorativeHingeCoversColor.color_img,
                    };
                }
            }
             if(conf.doorHardware.recessedPull) {
                const recessedPull = this.configuratorsDataService.get('recessedPulls', conf.doorHardware.recessedPull);
                if (recessedPull) {
                    conf.doorHardwareDictionary.doorHardware[recessedPull.id] = {
                        id: recessedPull.id,
                        name: recessedPull.name,
                    };

                    if (conf.doorHardware.recessedPullColor && conf.doorHardware.recessedPullColor.color) {
                        const selectedVisualization = this.getRecessedPullVisualization(
                            recessedPull,
                            conf.doorHardware.recessedPullColor
                        );
                        conf.doorHardware.visualizations.recessedPull = selectedVisualization;
                    } else {
                        conf.doorHardware.visualizations.recessedPull = recessedPull.visualizations[-1] ?? null;
                    }
                }
            }

            if(conf.doorHardware.recessedPullColor) {
                conf.doorHardwareDictionary.accessoryColors[Number(conf.doorHardware.recessedPullColor.color.id)] = {
                    id: Number(conf.doorHardware.recessedPullColor.color.id),
                    name: conf.doorHardware.recessedPullColor.color.name as any,
                    color: conf.doorHardware.recessedPullColor.color.color,
                    code: conf.doorHardware.recessedPullColor.color.code,
                    colorImg: 'color_img' in conf.doorHardware.recessedPullColor.color ? conf.doorHardware.recessedPullColor.color.color_img : conf.doorHardware.recessedPullColor.color.colorImg,
                };
            }
        }
    }

    private itemHasCategoryMapping(item: any): boolean {
        if (!item?.window_accessories_category_id) {
            return false;
        }
        const categories = this.configuratorsDataService.data.windowAccessoriesCategories || [];
        const category = categories.find(
            (c) => String(c.id) === String(item.window_accessories_category_id)
        );
        return !!category?.color_mapping_source;
    }

    private findColorFromCategoryMapping(
        item: any,
        conf: DoorActiveConfiguration
    ): IccAccessoryColor | null {
        if (!item?.window_accessories_category_id || !isArray(item.colors_ids)) {
            return null;
        }
        const categories = this.configuratorsDataService.data.windowAccessoriesCategories || [];
        const category = categories.find(
            (c) => String(c.id) === String(item.window_accessories_category_id)
        );
        if (!category?.color_mapping_source) {
            return null;
        }


        const availableColors = this.hardwareColors.filter(
            (c) => item.colors_ids.some((id: string | number) => Number(id) === Number(c.id))
        );
        if (!availableColors.length) {
            return null;
        }
        const findByConstrColorId = (constrColorId: number | string | undefined): IccAccessoryColor | undefined => {
            if (!constrColorId) return undefined;
            const mappedIds = this.colorMappingService.getMappedHandleColorIds(constrColorId);
            return availableColors.find(color => mappedIds.some(id => id === Number(color.id)));
        };

        if (
            category.color_mapping_source !== 'accessory'
            && category.color_mapping_source !== 'accessory_category'
        ) {
            const constrColorId = this.colorMappingService.getWindowColorId(conf, 'inner');
            const matched = findByConstrColorId(constrColorId);
            const color = core.copy(matched ?? availableColors[0]);
            color.isDefault = true;
            return color;
        }

        if (
            category.color_mapping_source === 'accessory'
            && category.color_mapping_accessory_id
        ) {
            const sourceAccessory = (conf.Accessories || []).find(
                (acc: any) => String(acc.accessory.id) === String(category.color_mapping_accessory_id)
            );
            if (sourceAccessory?.accessory?.selectedColor != null) {
                const srcSelected = sourceAccessory.accessory.selectedColor;
                if (typeof srcSelected !== 'string') {
                    const constrColorId = (srcSelected as any)?.outer?.id;
                    const matched = findByConstrColorId(constrColorId);
                    const color = core.copy(matched ?? availableColors[0]);
                    color.isDefault = true;
                    return color;
                } else {
                    const same = availableColors.find(c => Number(c.id) === Number(srcSelected));
                    const color = core.copy(same ?? availableColors[0]);
                    color.isDefault = true;
                    return color;
                }
            }
        }

        if (
            category.color_mapping_source === 'accessory_category'
            && category.color_mapping_accessory_category_id
        ) {
            const sourceAccessory = (conf.Accessories || []).find(
                (acc: any) =>
                    String(acc.accessory.window_accessories_category_id)
                    === String(category.color_mapping_accessory_category_id)
            );
            if (sourceAccessory?.accessory?.selectedColor != null) {
                const srcSelected = sourceAccessory.accessory.selectedColor;
                if (typeof srcSelected !== 'string') {
                    const constrColorId = (srcSelected as any)?.outer?.id;
                    const matched = findByConstrColorId(constrColorId);
                    const color = core.copy(matched ?? availableColors[0]);
                    color.isDefault = true;
                    return color;
                } else {
                    const same = availableColors.find(c => Number(c.id) === Number(srcSelected));
                    const color = core.copy(same ?? availableColors[0]);
                    color.isDefault = true;
                    return color;
                }
            }
        }

        const fallback = core.copy(availableColors[0]);
        fallback.isDefault = true;
        return fallback;
    }

    getContextInfo(conf = this.configurationsService.conf.Current, type: string) {
        const infoContext = conf.InfoContext.find((i) => i.valueType === type);
        return infoContext ? infoContext.value : false;
    }

    getSelectedFilling(conf: DoorActiveConfiguration) {
        const doorActiveSash = conf.Sashes.find(s => ['DRA', 'DOA'].includes(s.type.type));
        const selectedFilling = doorActiveSash && doorActiveSash.glazing?.id ? doorActiveSash.glazing : conf.Model;
        return selectedFilling;
    }

    getRecessedPulls(conf = this.configurationsService.conf.Current) {
        const selectedFilling = this.getSelectedFilling(conf);

        if(!selectedFilling || !selectedFilling.has_recessed_pull) {
            return [];
        }

        const outerSashColor = this.newColorService.getElementColor(conf, {element: 'sash', side: 'outer'});
        if(outerSashColor) {
            return this.recessedPulls.filter(recessedPull =>
                this.isRecessedPullBasedOnDependenciesAvailable(recessedPull.id)
                && recessedPull.color_related_to_sash.some(colorRelatedToSash =>
                    colorRelatedToSash.colorGroup.some(colorGroup =>
                        outerSashColor.colorGroups.includes(Number(colorGroup))
                    )
                )
            );
        }
        return []

    }


    getRecessedPullColors(conf = this.configurationsService.conf.Current) : {colorSource: string, colors: IccColor[] | IccAccessoryColor[], price: number, list_price: number}[] {
        const availableColors = [];
        const recessedPull = this.recessedPulls.find(t => Number(t.id) === conf.doorHardware.recessedPull)
        if(!recessedPull) {
            return availableColors;
        }
        const outerSashColor = this.newColorService.getElementColor(conf, {element: 'sash', side: 'outer'});
        const outerSashColorGroups = outerSashColor?.colorGroups;
        let recessedPullAvailableColors = recessedPull?.color_related_to_sash;
        recessedPullAvailableColors = recessedPullAvailableColors.filter(c => c.colorGroup.some(colorGroupId => outerSashColorGroups.includes(Number(colorGroupId))));
        recessedPullAvailableColors.forEach(colors => {
            if(colors.mode === 'sash') {
                availableColors.push({
                    colorSource: colors.mode,
                    colors: [outerSashColor],
                    price: colors.price,
                    list_price: colors.list_price
                });
            } else if(colors.mode === 'color_group') {
                availableColors.push({
                    colorSource: colors.mode,
                    colors: Object.values(this.newColorService.colors).filter(color =>
                        colors.selectedColor?.some(colorGroupId =>
                            color.colorGroups.includes(Number(colorGroupId))
                        )),
                    price: colors.price,
                    list_price: colors.list_price
                });
            } else if (colors.mode === 'accessory_color') {
                availableColors.push({
                    colorSource: colors.mode,
                    colors: this.hardwareColors.filter(hardwareColor => colors.selectedColor.map(Number).includes(Number(hardwareColor.id))),
                    price: colors.price,
                    list_price: colors.list_price
                });
            }
        });

        return availableColors;

    }

    /**
     * Pobiera wizualizację dla uchwytu wpuszczanego na podstawie hierarchii priorytetów
     * @param recessedPull - obiekt uchwytu wpuszczanego
     * @param recessedPullColor - konfiguracja koloru uchwytu
     * @returns wybraną wizualizację lub null
     */
    private getRecessedPullVisualization(recessedPull: any, recessedPullColor: any): any {
        const colorSource = recessedPullColor.colorSource === 'sash' ? 'color_group' : recessedPullColor.colorSource;
        const colorId = recessedPullColor.color.id;
        const colorGroups = recessedPullColor.color.colorGroups || [];

        if (colorSource === 'color_group' && colorGroups.length > 0) {
            return this.getColorGroupVisualization(recessedPull, colorGroups, colorId);
        } else if (colorSource === 'accessory_color') {
            return this.getAccessoryColorVisualization(recessedPull, colorId);
        } else {
            return this.getOtherColorSourceVisualization(recessedPull, colorSource, colorId);
        }
    }

    private getColorGroupVisualization(recessedPull: any, colorGroups: number[], colorId: number): any {

        for (const groupId of colorGroups) {
            const exactKey = `color_group#${groupId}#${colorId}`;
            if (recessedPull.visualizations[exactKey]) {
                return recessedPull.visualizations[exactKey];
            }
        }
        for (const groupId of colorGroups) {
            const groupKey = `color_group#${groupId}#-1`;
            if (recessedPull.visualizations[groupKey]) {
                return recessedPull.visualizations[groupKey];
            }
        }

        const colorKey = `color_group#-1#${colorId}`;
        if (recessedPull.visualizations[colorKey]) {
            return recessedPull.visualizations[colorKey];
        }

        const generalKey = 'color_group#-1#-1';
        if (recessedPull.visualizations[generalKey]) {
            return recessedPull.visualizations[generalKey];
        }

        return recessedPull.visualizations[-1] || null;
    }

    private getAccessoryColorVisualization(recessedPull: any, colorId: number): any {

        const exactKey = `accessory_color#${colorId}`;
        if (recessedPull.visualizations[exactKey]) {
            return recessedPull.visualizations[exactKey];
        }

        const generalKey = 'accessory_color#-1';
        if (recessedPull.visualizations[generalKey]) {
            return recessedPull.visualizations[generalKey];
        }


        return recessedPull.visualizations[-1] || null;
    }

    private getOtherColorSourceVisualization(recessedPull: any, colorSource: string, colorId: number): any {
        const visualizationKey = colorSource + '-' + colorId;
        return recessedPull.visualizations[visualizationKey] ||
               recessedPull.visualizations[colorSource + '#-1'] ||
               recessedPull.visualizations[-1] ||
               null;
    }
}
